// src/components/EditSuperImposableLoad.js
import React, { useState, useEffect, useRef, useLayoutEffect, useMemo } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import axios from "../axiosConfig";
import "../styles/EditSuperImposableLoad.css";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { customSelectStyles } from "../styles/reactSelectStyles.js";
import { FaCalendarAlt, FaClock, FaCheckCircle } from "react-icons/fa";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { useTranslation } from "react-i18next";

const CURRENCY_OPTIONS = [
  { value: "EUR", label: "EUR" },
  { value: "USD", label: "USD" },
  { value: "GBP", label: "GBP" },
];

const formatInputDate = (isoDate) => {
  if (!isoDate) return "";
  const dateObj = new Date(isoDate);
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const MAX_HEIGHT = 2.7;
const MAX_WIDTH = 2.5;
const MIN_LENGTH = 0.01;

const displayAuditEmail = (value) => {
  const email = String(value || "").trim();
  return email || "—";
};

// Normaliza um ponto garantindo todos os campos e flags
const normalizePoint = (p = {}) => ({
  location: p.location || "",
  date: p.date || "",
  hour: p.hour || "",

  // 🆕 point-level dimensions
  length: p.length ?? "",
  width: p.width ?? "",
  height: p.height ?? "",
  lengthError: "",
  widthError: "",
  heightError: "",

  placeId: p.placeId ?? null,
  address: p.address || "",
  postalCode: p.postalCode || "",
  city: p.city || "",
  country: p.country || "",
  gMapsLink: p.gMapsLink || "",
  coordinates: p.coordinates || "",
  contactPhone: p.contactPhone || "",

  // 🆕 loadingAccess
  loadingAccess: p.loadingAccess ?? null,

  // 🆕 handling equipment (pickup/delivery)
  handlingEquipment: p.handlingEquipment ?? null,
  handlingEquipmentOther: p.handlingEquipmentOther ?? "",

  // 🆕 reference (optional)
  reference: p.reference ?? "",

  // flags de erro sempre presentes
  locationError: false,
  dateError: false,
  hourError: false,
  addressError: false,
  postalCodeError: false,
  cityError: false,
  countryError: false,
  contactPhoneError: false,
  gMapsLinkError: false,
  coordinatesError: false,
  loadingAccessError: false,

  // 🆕 errors
  handlingEquipmentError: false,
  handlingEquipmentOtherError: false,

  showMoreFields: false,
});

// Mapeia coleções
const mapPoints = (arr) => (Array.isArray(arr) ? arr.map(normalizePoint) : []);

// Helper para os selects de lugares (com fallback quando as options ainda não chegaram)
export const resolvePlaceSelectValue = (place, placeOptions) => {
  if (!place) return null;

  // 1) Tenta por ID (tipos podem variar, por isso String)
  if (place.placeId != null) {
    const byId = placeOptions.find((o) => String(o.value) === String(place.placeId));
    if (byId) return byId;
  }

  // 2) Sem ID → tenta por label/location normalizado
  const norm = (s) => (s || "").trim().toLowerCase();
  if (place.location) {
    const byLabel = placeOptions.find((o) => norm(o.label) === norm(place.location));
    if (byLabel) return byLabel;

    // 3) Último recurso → sintetizar uma "ghost option" com meta
    return {
      label: place.location,
      value: place.location,
      metaCity: place.city || "",
      metaCountry: place.country || "",
    };
  }

  return null;
};

const resolveIdSelectValue = (id, options, fallbackLabel) => {
  if (!id) return null;
  const opt = options.find((o) => o.value === Number(id) || o.value === id);
  return opt || (fallbackLabel ? { label: fallbackLabel, value: id } : null);
};

function EditSuperImposableLoad() {
  const { t } = useTranslation("edit_superimposable_load");
  const pageTopRef = useRef(null);

  const loadingAccessOptions = [
    { value: "SIDE", label: t("loadingAccess.options.side", { defaultValue: "Side" }) },
    { value: "REAR", label: t("loadingAccess.options.rear", { defaultValue: "Rear" }) },
    { value: "TOP",  label: t("loadingAccess.options.top",  { defaultValue: "Top" }) },
    { value: "DOCK", label: t("loadingAccess.options.dock", { defaultValue: "Dock" }) },
    { value: "ANY",  label: t("loadingAccess.options.any",  { defaultValue: "Any" }) },
  ];

  const handleLoadingAccessChange = (index, opt, isDelivery) => {
    const value = opt?.value ?? null;
    if (isDelivery) {
      const updated = [...deliveryPlaces];
      updated[index] = {
        ...updated[index],
        loadingAccess: value,
        loadingAccessError: false,
      };
      setDeliveryPlaces(updated);
    } else {
      const updated = [...pickUpPlaces];
      updated[index] = {
        ...updated[index],
        loadingAccess: value,
        loadingAccessError: false,
      };
      setPickUpPlaces(updated);
    }
  };

  const handlingEquipmentOptions = [
    { value: "FORKLIFT",            label: t("fields.handlingEquipment.forklift", { defaultValue: "Forklift" }) },
    { value: "PALLET_JACK",         label: t("fields.handlingEquipment.palletJack", { defaultValue: "Pallet jack" }) },
    { value: "ELECTRIC_PALLET_JACK",label: t("fields.handlingEquipment.electricPalletJack", { defaultValue: "Electric pallet jack" }) },
    { value: "CRANE",               label: t("fields.handlingEquipment.crane", { defaultValue: "Crane" }) },
    { value: "LOADING_RAMP",        label: t("fields.handlingEquipment.loadingRamp", { defaultValue: "Loading ramp" }) },
    { value: "TAIL_LIFT",           label: t("fields.handlingEquipment.tailLift", { defaultValue: "Tail lift" }) },
    { value: "HAND_LOAD",           label: t("fields.handlingEquipment.handLoad", { defaultValue: "Hand load" }) },
    { value: "CONVEYOR",            label: t("fields.handlingEquipment.conveyor", { defaultValue: "Conveyor belt" }) },
    { value: "OTHER",               label: t("fields.handlingEquipment.other", { defaultValue: "Other" }) },
  ];

  const handleHandlingEquipmentChange = (index, opt, isDelivery) => {
    const value = opt?.value ?? null;

    if (isDelivery) {
      setDeliveryPlaces((prev) => {
        const updated = [...prev];
        if (!updated[index]) return prev;

        updated[index] = {
          ...updated[index],
          handlingEquipment: value,
          handlingEquipmentError: false,
          // se trocar para não-OTHER limpa o texto
          handlingEquipmentOther: value === "OTHER" ? (updated[index].handlingEquipmentOther || "") : "",
          handlingEquipmentOtherError: false,
        };
        return updated;
      });
    } else {
      setPickUpPlaces((prev) => {
        const updated = [...prev];
        if (!updated[index]) return prev;

        updated[index] = {
          ...updated[index],
          handlingEquipment: value,
          handlingEquipmentError: false,
          handlingEquipmentOther: value === "OTHER" ? (updated[index].handlingEquipmentOther || "") : "",
          handlingEquipmentOtherError: false,
        };
        return updated;
      });
    }
  };

  const CLIENT_TYPE_OPTIONS = [
    { label: t("clientType.industry"), value: "industry" },
    { label: t("clientType.carrier"), value: "carrier" },
  ];

  const trailerTypeOptions = [
    { value: "ANY", label: t("fields.trailerType.options.any", { defaultValue: "Any" }) },

    { value: "TAUTLINER", label: t("fields.trailerType.options.tautliner", { defaultValue: "Tautliner / Curtain" }) },
    { value: "REEFER", label: t("fields.trailerType.options.reefer", { defaultValue: "Reefer / Frigo" }) },
    { value: "BOX", label: t("fields.trailerType.options.box", { defaultValue: "Box" }) },
    { value: "FLATBED", label: t("fields.trailerType.options.flatbed", { defaultValue: "Flatbed" }) },
    { value: "LOWBED", label: t("fields.trailerType.options.lowbed", { defaultValue: "Lowbed" }) },
    { value: "TANKER", label: t("fields.trailerType.options.tanker", { defaultValue: "Tanker" }) },
    { value: "TIPPER", label: t("fields.trailerType.options.tipper", { defaultValue: "Tipper" }) },
    { value: "CONTAINER_CHASSIS", label: t("fields.trailerType.options.containerChassis", { defaultValue: "Container chassis" }) },
    { value: "MEGA", label: t("fields.trailerType.options.mega", { defaultValue: "Mega" }) },

    { value: "OTHER", label: t("fields.trailerType.options.other", { defaultValue: "Other" }) },
  ];

  const { loadId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state?.prefill ?? null;

  const handleCloseSuccessPopup = () => {
    setSuccessPopupOpen(false);
    navigate("/loads");
  };
  
  // ✅ Always open edit at top (hard override)
  useEffect(() => {
    // Disable browser scroll restoration on this screen
    const prev = window.history.scrollRestoration;
    try {
      window.history.scrollRestoration = "manual";
    } catch {}

    return () => {
      try {
        window.history.scrollRestoration = prev || "auto";
      } catch {}
    };
  }, []);

  const scrollToTopHard = () => {
    // 0) anchor first (if present)
    pageTopRef.current?.scrollIntoView({ behavior: "auto", block: "start" });

    // 1) immediate
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    // 2) next paint (React layout settles)
    requestAnimationFrame(() => {
      pageTopRef.current?.scrollIntoView({ behavior: "auto", block: "start" });

      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;

      // 3) a tiny delay to beat late restores / layout shifts
      setTimeout(() => {
        pageTopRef.current?.scrollIntoView({ behavior: "auto", block: "start" });

        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }, 50);
    });
  };

  useLayoutEffect(() => {
    scrollToTopHard();
  }, [loadId]);

  const [load, setLoad] = useState(prefill || null);
  const [length, setLength] = useState(prefill?.length ?? "");
  const [height, setHeight] = useState(prefill?.height ?? "");
  const [width, setWidth] = useState(prefill?.width ?? "");
  const [weight, setWeight] = useState(prefill?.weight ?? "");
  const [maxAllowedLength, setMaxAllowedLength] = useState(0);
  const [maxAllowedHeight, setMaxAllowedHeight] = useState(0);
  const [maxAllowedWidth, setMaxAllowedWidth] = useState(0);
  const [loading, setLoading] = useState(false);
  const [successPopupOpen, setSuccessPopupOpen] = useState(false);

  // Linhas pré-preenchidas com prefill (sem placeholders)
  const [pickUpPlaces, setPickUpPlaces] = useState(
    prefill ? mapPoints(prefill.pickupLocations || prefill.pickupPoints) : []
  );
  const [deliveryPlaces, setDeliveryPlaces] = useState(
    prefill ? mapPoints(prefill.deliveryLocations || prefill.deliveryPoints) : []
  );

  // Counters a mostrar (com vários fallbacks possíveis)
  const tsCounter =
    load?.transportSession?.transportSessionNumber ??
    load?.transportSessionNumber ??
    prefill?.transportSession?.transportSessionNumber ??
    prefill?.transportSessionNumber ??
    prefill?.tsUiCounter ??
    load?.tsUiCounter ??
    null;

  const loadCounter =
    load?.loadNumber ?? prefill?.loadNumber ?? prefill?.uiCounter ?? load?.uiCounter ?? null;

  // Garantir normalização do prefill antes do 1º paint
  useLayoutEffect(() => {
    if (!prefill) return;
    setPickUpPlaces(mapPoints(prefill.pickupLocations || prefill.pickupPoints));
    setDeliveryPlaces(mapPoints(prefill.deliveryLocations || prefill.deliveryPoints));
  }, [prefill]);

  const [pickUpPlaceError, setPickUpPlaceError] = useState("");
  const [deliveryPlaceError, setDeliveryPlaceError] = useState("");
  const [invalidDeliveryIndexes, setInvalidDeliveryIndexes] = useState([]);
  const [loadBearerEarliestPickup, setLoadBearerEarliestPickup] = useState(null);
  const [loadBearerEarliestDelivery, setLoadBearerEarliestDelivery] = useState(null);
  const today = new Date().toISOString().split("T")[0];

  const [invalidPickupBearerIndexes, setInvalidPickupBearerIndexes] = useState([]);
  const [existingDateHourConflicts, setExistingDateHourConflicts] = useState([]);
  const [selectedCarrier, setSelectedCarrier] = useState(null);
  const [allCarriers, setAllCarriers] = useState([]);
  const [carrierError, setCarrierError] = useState("");
  const [carrierIdError, setCarrierIdError] = useState("");

  // Individual error states
  const [lengthError, setLengthError] = useState("");
  const [heightError, setHeightError] = useState("");
  const [widthError, setWidthError] = useState("");
  const [weightError, setWeightError] = useState("");

  const [price, setPrice] = useState(prefill?.price ?? "");
  const [priceCurrency, setPriceCurrency] = useState(prefill?.priceCurrency ?? "EUR");
  const [priceError, setPriceError] = useState("");

  // 🆕 Price for subcontracted (optional, only when external carrier selected)
  const [price4subcontractedInput, setPrice4subcontractedInput] = useState(
    prefill?.price4subcontrated ?? prefill?.price4subcontracted ?? ""
  );
  const [price4subcontractedError, setPrice4subcontractedError] = useState("");

  // 📝 Notes for driver (optional)
  const [notes4driver, setNotes4driver] = useState(prefill?.notes4driver ?? "");
  const [notes4driverError, setNotes4driverError] = useState("");

  // 🆕 Notes for subcontracted (optional, only when external carrier selected)
  const [notes4subcontracted, setNotes4subcontracted] = useState(prefill?.notes4subcontracted ?? "");
  const [notes4subcontractedError, setNotes4subcontractedError] = useState("");

  // 📦 Merchandise (required)
  const [merchandise, setMerchandise] = useState(prefill?.merchandise ?? "");
  const [merchandiseError, setMerchandiseError] = useState("");

  // 🆕 Reference (optional)
  const [reference, setReference] = useState(prefill?.reference ?? "");
  const [referenceError, setReferenceError] = useState("");

  // 🆕 Trailer Type (required)
  const [trailerType, setTrailerType] = useState(prefill?.trailerType ?? "");

  const isTrailerOther = useMemo(() => {
    return String(trailerType || "").trim().toUpperCase() === "OTHER";
  }, [trailerType]);

  const [trailerTypeOther, setTrailerTypeOther] = useState(prefill?.trailerTypeOther ?? "");
  const [trailerTypeError, setTrailerTypeError] = useState("");
  const [trailerTypeOtherError, setTrailerTypeOtherError] = useState("");

  const [carrierId, setCarrierId] = useState(prefill?.carrierId ?? "");

  // 🧾 Current company id (tenant) for comparison
  const [companyId, setCompanyId] = useState(null);
  const [companyName, setCompanyName] = useState("");

  const roundToTwo = (num) => Math.round(num * 100) / 100;

    // 🆕 fetch companyId from whoami (tenant)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get("/auth/whoami");
        if (!alive) return;

        const cid =
          res.data?.companyId ??
          res.data?.company_id ??
          res.data?.company?.id ??
          res.data?.userCompanyRaw ??
          res.data?.reqCompanyId ??
          null;

        setCompanyId(cid != null ? String(cid) : null);

        const cname =
          res.data?.company?.name ??
          res.data?.companyName ??
          res.data?.company_name ??
          "";

        setCompanyName(String(cname || ""));

      } catch (e) {
        console.warn("whoami fetch failed:", e?.response?.status);
        setCompanyId(null);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  // 🆕 show notes4subcontracted ONLY if carrierId differs from companyId
  const showNotes4subcontracted = useMemo(() => {
    if (!carrierId) return false;

    // handle your internal sentinel if it ever appears here
    if (String(carrierId) === "__internal__") return false;

    if (companyId != null && String(carrierId) === String(companyId)) return false;

    return true;
  }, [carrierId, companyId]);

  const internalCarrierOption = useMemo(() => {
    const name = String(companyName || "").trim();
    return {
      value: "__internal__",
      label: name ? `${name} (internal)` : t("labels.internalCarrierFallback", { defaultValue: "Internal (carrier)" }),
    };
  }, [companyName, t]);

  // 🧼 if user switches back to internal carrier, clear subcontracted notes
  useEffect(() => {
    if (!showNotes4subcontracted) {
      setNotes4subcontracted("");
      setNotes4subcontractedError("");
      setPrice4subcontractedInput("");
      setPrice4subcontractedError("");
      setSelectedSubcontractedEmails([]);
    }
  }, [showNotes4subcontracted]);

  const dateRefs = useRef([]);
  const timeRefs = useRef([]);
  const hourRefs = useRef([]);
  const deliveryDateRefs = useRef([]);
  const deliveryHourRefs = useRef([]);
  const deliveryTimeRefs = useRef([]);

  const [invalidFields, setInvalidFields] = useState({ pickups: [], deliveries: [] });
  const [fieldValidationError, setFieldValidationError] = useState("");
  const [industryOptions, setIndustryOptions] = useState([]);
  const [carrierOptions, setCarrierOptions] = useState([]);
  const [invalidBearerDeliveryConflicts, setInvalidBearerDeliveryConflicts] = useState([]);
  const [clientFieldError, setClientFieldError] = useState("");
  const [invalidBearerDeliveryHourIndexes, setInvalidBearerDeliveryHourIndexes] = useState([]);
  const [deliveryBeforePickupErrors, setDeliveryBeforePickupErrors] = useState({ date: [], hour: [] });
  const [placeOptions, setPlaceOptions] = useState([]);
  const [selectedLoadBearer, setSelectedLoadBearer] = useState(null);

  // 👉 Prefill imediato para Client/Carrier (aparece logo no 1º paint)
  const [clientTypeError, setClientTypeError] = useState("");
  const [clientSelectionError, setClientSelectionError] = useState("");
  const [clientType, setClientType] = useState(
    prefill?.industryClientId ? "industry" : prefill?.carrierClientId ? "carrier" : ""
  );
  const [industryClientId, setIndustryClientId] = useState(prefill?.industryClientId ?? "");
  const [carrierClientId, setCarrierClientId] = useState(prefill?.carrierClientId ?? "");
  const [carrierOptsRaw, setCarrierOptsRaw] = useState([]);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [transportCarrierError, setTransportCarrierError] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [hasError, setHasError] = useState(false);
  const MAX_POINTS = 7;

  const [selectedClientContactEmails, setSelectedClientContactEmails] = useState([]);

  const isValidEmail = (value) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
  };

  const emailsToOptions = (raw) => {
    if (!raw) return [];

    const arr = Array.isArray(raw)
      ? raw
      : String(raw)
          .split(",")
          .map((x) => x.trim());

    return arr
      .map((email) => String(email || "").trim().toLowerCase())
      .filter((email) => email && isValidEmail(email))
      .slice(0, 3)
      .map((email) => ({ value: email, label: email }));
  };

  const [selectedSubcontractedEmails, setSelectedSubcontractedEmails] = useState(() => {
    const raw =
      prefill?.subcontractedEmails ??
      prefill?.subcontractedEmail ??
      [];

    if (Array.isArray(raw)) {
      return raw
        .map((email) => {
          const e = String(email || "").trim().toLowerCase();
          return e && isValidEmail(e) ? { value: e, label: e } : null;
        })
        .filter(Boolean)
        .slice(0, 3);
    }

    if (typeof raw === "string" && raw.trim()) {
      return raw
        .split(",")
        .map((email) => String(email || "").trim().toLowerCase())
        .filter((email) => isValidEmail(email))
        .map((email) => ({ value: email, label: email }))
        .slice(0, 3);
    }

    return [];
  });

  const extractClientEmails = (clientObj) => {
    if (!clientObj || typeof clientObj !== "object") return [];

    const found = new Set();

    const addEmail = (value) => {
      const email = String(value || "").trim().toLowerCase();
      if (!email) return;
      if (!isValidEmail(email)) return;
      found.add(email);
    };

    const contactsRaw = clientObj.contacts;
    let contacts = [];

    if (Array.isArray(contactsRaw)) {
      contacts = contactsRaw;
    } else if (typeof contactsRaw === "string") {
      try {
        const parsed = JSON.parse(contactsRaw);
        if (Array.isArray(parsed)) contacts = parsed;
      } catch {
        contacts = [];
      }
    }

    contacts.forEach((contact) => {
      addEmail(contact?.contactEmail);
    });

    return Array.from(found).map((email) => ({
      value: email,
      label: email,
    }));
  };

  const selectedClientContactEmailOptions = useMemo(() => {
    if (!clientType) return [];

    const selectedId =
      clientType === "industry"
        ? selectedClient?.value || industryClientId
        : selectedClient?.value || carrierClientId;

    if (!selectedId) return [];

    const sourceList = clientType === "industry" ? industryOptions : carrierOptions;
    const fullClient = (sourceList || []).find(
      (c) => String(c.value || c.id) === String(selectedId)
    );

    return extractClientEmails(fullClient?.raw || null);
  }, [clientType, selectedClient, industryClientId, carrierClientId, industryOptions, carrierOptions]);

  const selectedSubcontractedEmailOptions = useMemo(() => {
    if (!showNotes4subcontracted) return [];
    if (!carrierId || String(carrierId) === "__internal__") return [];
  
    const fullCarrier = (carrierOptions || []).find(
      (c) => String(c.value || c.id) === String(carrierId)
    );
  
    return extractClientEmails(fullCarrier?.raw || null);
  }, [showNotes4subcontracted, carrierId, carrierOptions]);

  const multiSelectHorizontalStyles = useMemo(() => {
    const base = customSelectStyles || {};

    return {
      ...base,

      control: (provided, state) => ({
        ...(base.control ? base.control(provided, state) : provided),
        minHeight: 44,
        height: "auto",
        overflow: "hidden",
        alignItems: "center",
      }),

      valueContainer: (provided, state) => ({
        ...(base.valueContainer ? base.valueContainer(provided, state) : provided),

        // ✅ tags sempre numa linha
        flexWrap: "nowrap",

        // ✅ scroll horizontal quando há várias opções
        overflowX: "auto",
        overflowY: "hidden",
        whiteSpace: "nowrap",

        // ✅ altura/alinhamento consistente
        alignItems: "center",
        paddingTop: 6,
        paddingBottom: 6,

        // ✅ espaço para clear + seta
        paddingRight: 72,

        gap: 6,
      }),

      multiValue: (provided, state) => ({
        ...(base.multiValue ? base.multiValue(provided, state) : provided),
        flex: "0 0 auto",
        maxWidth: "none",
        margin: 0,
      }),

      multiValueLabel: (provided, state) => ({
        ...(base.multiValueLabel ? base.multiValueLabel(provided, state) : provided),
        maxWidth: 160,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }),

      indicatorsContainer: (provided, state) => ({
        ...(base.indicatorsContainer ? base.indicatorsContainer(provided, state) : provided),
        alignItems: "center",
        paddingTop: 0,
      }),

      menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
      }),
    };
  }, []);

  const carrierOptionsForSelect = useMemo(() => {
    return [internalCarrierOption, ...(Array.isArray(carrierOptsRaw) ? carrierOptsRaw : [])];
  }, [internalCarrierOption, carrierOptsRaw]);

  // substitui o teu renderPlaceOption por este
  const renderPlaceOption = (opt, { context }) => {
    const hasSub = opt.metaCity || opt.metaCountry;
    return (
      <div style={{ lineHeight: 1.2 }}>
        <div className="rs-option-main">{opt.label}</div>
        {hasSub && (
          <div className={`rs-option-sub ${context === "value" ? "rs-option-sub--value" : ""}`}>
            {opt.metaCity}
            {opt.metaCity && opt.metaCountry ? ", " : ""}
            {opt.metaCountry}
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    deliveryDateRefs.current = deliveryPlaces.map((_, i) => deliveryDateRefs.current[i] || null);
    deliveryHourRefs.current = deliveryPlaces.map((_, i) => deliveryHourRefs.current[i] || null);
    dateRefs.current = pickUpPlaces.map((_, i) => dateRefs.current[i] || null);
    hourRefs.current = pickUpPlaces.map((_, i) => hourRefs.current[i] || null);
  }, [pickUpPlaces, deliveryPlaces]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // 1) Fetch paralelo (sem bloquear render)
        const [placesRes, industriesRes, carriersRes, loadRes] = await Promise.all([
          axios.get("/places"),
          axios.get("/industries"),
          axios.get("/carriers"),
          axios.get(`/loads/${loadId}/editloads`),
        ]);

        if (cancelled) return;

        // 2) Options
        const placeOpts = placesRes.data.map((p) => ({
          value: String(p.id), // mantém consistente como string
          label: p.placeName,
          metaCity: p.city || "",
          metaCountry: p.country || "",
          fullData: p,
        }));
        const industryOpts = industriesRes.data.map((i) => ({
          value: i.id,
          label: i.companyName,
          raw: i,
        }));

        const carrierOpts = carriersRes.data.map((c) => ({
          value: c.id,
          label: c.companyName,
          raw: c,
        }));

        // ✅ internal carrier option (igual EditNormalLoad/EditLoadBearers)
        const internalOpt =
          companyName
            ? { value: "__internal__", label: `${companyName} (internal)` }
            : { value: "__internal__", label: t("labels.internalCarrierFallback", { defaultValue: "Internal (carrier)" }) };

        const carrierOptsWithInternal = [internalOpt, ...carrierOpts];

        const data = loadRes.data;
        if (!data.superImposable) {
          setLengthError(t("errors.notSuperImposable"));
          return;
        }
        if (!clientType) {
          if (data.industryClientId) setClientType("industry");
          else if (data.carrierClientId) setClientType("carrier");
        }

        setSelectedClientContactEmails((prev) => {
          if (Array.isArray(prev) && prev.length > 0) return prev;
          return emailsToOptions(data.clientContactEmails);
        });

        // 3) Mescla load + merch (sem esconder form)
        setLoad((prev) => ({
          ...(prev || {}),
          ...(data || {}),
          createdByEmail: data?.createdByEmail ?? prev?.createdByEmail ?? "",
          updatedByEmail: data?.updatedByEmail ?? prev?.updatedByEmail ?? "",
        }));
        setLength((prev) => prev || data.length || "");
        setHeight((prev) => prev || data.height || "");
        setWidth((prev) => prev || data.width || "");
        setWeight((prev) => (String(prev).trim().length ? prev : (data.weight ?? ""))); // 🆕
        setPrice((prev) => (String(prev).trim().length ? prev : data.price ?? ""));
        setPriceCurrency((prev) => (prev ? prev : data.priceCurrency ?? "EUR"));
        setPrice4subcontractedInput((prev) =>
          String(prev).trim().length ? prev : (data.price4subcontrated ?? data.price4subcontracted ?? "")
        );

        // 🆕 prefill notes
        setNotes4driver((prev) =>
          typeof prev === "string" && prev.length ? prev : data.notes4driver ?? ""
        );

        setNotes4subcontracted((prev) =>
          typeof prev === "string" && prev.length ? prev : data.notes4subcontracted ?? ""
        );

        setSelectedSubcontractedEmails((prev) => {
          if (Array.isArray(prev) && prev.length > 0) return prev;
        
          const raw =
            data.subcontractedEmails ??
            data.subcontractedEmail ??
            [];
        
          if (Array.isArray(raw)) {
            return raw
              .map((email) => {
                const e = String(email || "").trim().toLowerCase();
                return e && isValidEmail(e) ? { value: e, label: e } : null;
              })
              .filter(Boolean)
              .slice(0, 3);
          }
        
          if (typeof raw === "string" && raw.trim()) {
            return raw
              .split(",")
              .map((email) => String(email || "").trim().toLowerCase())
              .filter((email) => isValidEmail(email))
              .map((email) => ({ value: email, label: email }))
              .slice(0, 3);
          }
        
          return [];
        });

        // 📦 prefill merchandise
        setMerchandise((prev) =>
          typeof prev === "string" && prev.trim().length ? prev : data.merchandise ?? ""
        );

        // 🆕 prefill reference
        setReference((prev) =>
          typeof prev === "string" && prev.trim().length ? prev : data.reference ?? ""
        );

        // 🆕 prefill trailer type
        setTrailerType((prev) => (String(prev).trim().length ? prev : (data.trailerType ?? "")));
        setTrailerTypeOther((prev) =>
          String(prev).trim().length ? prev : (data.trailerTypeOther ?? "")
        );

        // 4) Options
        setPlaceOptions(placeOpts);
        setIndustryOptions(industryOpts);
        setCarrierOptions(carrierOpts);

        setCarrierOptsRaw(
          carriersRes.data.map((c) => ({
            value: c.id,
            label: c.companyName,
          }))
        );

        // 5) Cliente (derivado do load) já com as options disponíveis (mantém prefill se existir)
        if (data.industryClientId) {
          setClientType("industry");
          setIndustryClientId(data.industryClientId);
          const selected = industryOpts.find((i) => i.value === Number(data.industryClientId));
          if (selected) setSelectedClient(selected);
        } else if (data.carrierClientId) {
          setClientType("carrier");
          setCarrierClientId(data.carrierClientId);
          const selected = carrierOpts.find((c) => c.value === Number(data.carrierClientId));
          if (selected) setSelectedClient(selected);
        }

        // ✅ backend pode devolver null para internal
        if (data.carrierId == null) {
          setCarrierId("__internal__");
        } else {
          setCarrierId(String(data.carrierId));
        }

        // 6) Pickups/Deliveries: usa prefill se já existir; senão carrega do backend
        setPickUpPlaces((prev) =>
          prev?.length ? prev : mapPoints(data.pickupLocations || data.pickupPoints)
        );
        setDeliveryPlaces((prev) =>
          prev?.length ? prev : mapPoints(data.deliveryLocations || data.deliveryPoints)
        );

        // 7) Conflitos + constraints em paralelo
        const conflictsPromise = data.transportsessionId
          ? axios.get(`/pickupdeliveries/${data.transportsessionId}`)
          : Promise.resolve(null);

        const constraintsPromise = data.associatedLoadBearerId
          ? (async () => {
              await fetchLoadBearerConstraints(data.associatedLoadBearerId);
            })()
          : Promise.resolve();

        const conflictsRes = await conflictsPromise;
        if (!cancelled && conflictsRes?.data) {
          setExistingDateHourConflicts(conflictsRes.data);
        }
      } catch (error) {
        console.error("Error bootstrapping EditSuperImposableLoad:", error);
        setLengthError(t("errors.fetchFailed"));
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadId]);

  useEffect(() => {
    if (clientType === "industry" && load?.industryClientId && industryOptions.length > 0) {
      const match = industryOptions.find((opt) => opt.value === load.industryClientId);
      if (match) setSelectedClient(match);
    }

    if (clientType === "carrier" && load?.carrierClientId && carrierOptions.length > 0) {
      const match = carrierOptions.find((opt) => opt.value === load.carrierClientId);
      if (match) setSelectedClient(match);
    }
  }, [clientType, load, industryOptions, carrierOptions]);

  useEffect(() => {
    if (load?.carrierId && allCarriers.length > 0) {
      const matchedCarrier = allCarriers.find((c) => c.value === load.carrierId);
      if (matchedCarrier) {
        setSelectedCarrier(matchedCarrier);
      }
    }
  }, [load, allCarriers]);
  
  useEffect(() => {
    if (!showNotes4subcontracted) {
      setSelectedSubcontractedEmails([]);
      return;
    }
  
    if (!selectedSubcontractedEmailOptions.length) {
      setSelectedSubcontractedEmails([]);
      return;
    }
  
    setSelectedSubcontractedEmails((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) {
        return selectedSubcontractedEmailOptions.slice(0, 3);
      }
  
      const allowed = new Set(
        selectedSubcontractedEmailOptions.map((opt) => String(opt.value))
      );
  
      return prev.filter((opt) => allowed.has(String(opt.value))).slice(0, 3);
    });
  }, [showNotes4subcontracted, selectedSubcontractedEmailOptions]);

  // ✅ Fetch Associated Load Bearer Constraints
  const fetchLoadBearerConstraints = async (loadBearerId) => {
    try {
      console.log(`🚛 Fetching Load Bearer constraints for ID: ${loadBearerId}`);

      const response = await axios.get(`/loads/${loadBearerId}`);
      const loadBearer = response.data;

      if (!loadBearer || !loadBearer.length) {
        console.warn("⚠️ Load Bearer data is missing or invalid:", loadBearer);
        return;
      }

      // ✅ Loads na mesma sessão
      const transportSessionResponse = await axios.get(
        `/loads?transportsessionId=${loadBearer.transportsessionId}`
      );
      const sessionLoads = transportSessionResponse.data;

      // ✅ SuperImposableLoads associados ao bearer (exclui o atual)
      const associatedSuperImposableLoads = sessionLoads.filter(
        (load) =>
          load.superImposable &&
          load.associatedLoadBearerId === loadBearerId &&
          load.id !== parseInt(loadId)
      );

      // ✅ Espaço usado e disponível
      const totalSuperImposableUsedLength = associatedSuperImposableLoads.reduce(
        (sum, load) => sum + (parseFloat(load.length) || 0),
        0
      );

      const availableLength = Math.max(0, parseFloat(loadBearer.length) - totalSuperImposableUsedLength);
      const availableHeight = Math.max(0, MAX_HEIGHT - parseFloat(loadBearer.height));
      const availableWidth = parseFloat(loadBearer.width) || MAX_WIDTH;

      setMaxAllowedLength(roundToTwo(availableLength));
      setMaxAllowedHeight(roundToTwo(availableHeight));
      setMaxAllowedWidth(roundToTwo(availableWidth));

      console.log(
        `✅ Constraints from Load Bearer - Max Length: ${availableLength}m, Max Height: ${availableHeight}m, Max Width: ${availableWidth}m`
      );

      // ✅ Datas mínimas
      const pickupDateTimes = (loadBearer.pickupLocations || [])
        .filter((p) => p.date && p.hour)
        .map((p) => new Date(`${p.date}T${p.hour}`));

      const deliveryDateTimes = (loadBearer.deliveryLocations || [])
        .filter((p) => p.date && p.hour)
        .map((p) => new Date(`${p.date}T${p.hour}`));

      setLoadBearerEarliestPickup(
        pickupDateTimes.length > 0
          ? pickupDateTimes.reduce((earliest, current) => (current < earliest ? current : earliest))
          : null
      );

      setLoadBearerEarliestDelivery(
        deliveryDateTimes.length > 0
          ? deliveryDateTimes.reduce((earliest, current) => (current < earliest ? current : earliest))
          : null
      );

      if (pickupDateTimes.length > 0) {
        const earliestPickup = pickupDateTimes.reduce((e, c) => (c < e ? c : e));
        console.log("📦 Earliest Load Bearer Pickup:", earliestPickup.toISOString());
      }

      if (deliveryDateTimes.length > 0) {
        const earliestDelivery = deliveryDateTimes.reduce((e, c) => (c < e ? c : e));
        console.log("🚚 Earliest Load Bearer Delivery:", earliestDelivery.toISOString());
      }
    } catch (error) {
      console.error("❌ Error fetching associated Load Bearer:", error);
    }
  };

  // ✅ Point-level dimensions validation
  const getPointDimensionError = (field, value) => {
    const n = Number(value);

    if (value === "" || value == null) return "";
    if (!Number.isFinite(n) || n <= 0) {
      if (field === "length") return t("errors.length.invalid");
      if (field === "height") return t("errors.height.invalid");
      if (field === "width") return t("errors.width.invalid");
      return t("errors.allRequiredFields");
    }

    if (field === "length") {
      if (n > maxAllowedLength) {
        return t("errors.lengthMax", { max: maxAllowedLength.toFixed(2) });
      }

      return "";
    }

    if (field === "height") {
      if (n > maxAllowedHeight) {
        return t("errors.heightMax", { max: maxAllowedHeight.toFixed(2) });
      }

      return "";
    }

    if (field === "width") {
      if (n > maxAllowedWidth) {
        return t("errors.widthMax", { max: maxAllowedWidth.toFixed(2) });
      }

      return "";
    }

    return "";
  };

  const calculatePointDimensionTotals = (points = []) => {
    return points.reduce(
      (acc, point) => {
        const pointLength = Number(point.length || 0);
        const pointWidth = Number(point.width || 0);
        const pointHeight = Number(point.height || 0);

        return {
          length: acc.length + (Number.isFinite(pointLength) ? pointLength : 0),
          width: Math.max(acc.width, Number.isFinite(pointWidth) ? pointWidth : 0),
          height: Math.max(acc.height, Number.isFinite(pointHeight) ? pointHeight : 0),
        };
      },
      { length: 0, width: 0, height: 0 }
    );
  };

  const pickupDimensionTotals = useMemo(() => {
    return calculatePointDimensionTotals(pickUpPlaces);
  }, [pickUpPlaces]);

  const deliveryDimensionTotals = useMemo(() => {
    return calculatePointDimensionTotals(deliveryPlaces);
  }, [deliveryPlaces]);

  const pointDimensionSummary = useMemo(() => {
    const pickupLength = Number(pickupDimensionTotals.length || 0);
    const deliveryLength = Number(deliveryDimensionTotals.length || 0);

    const lengthMatches =
      Math.round(pickupLength * 100) === Math.round(deliveryLength * 100);

    return {
      length: pickupLength,
      pickupLength,
      deliveryLength,
      width: Math.max(pickupDimensionTotals.width, deliveryDimensionTotals.width),
      height: Math.max(pickupDimensionTotals.height, deliveryDimensionTotals.height),
      lengthMatches,
    };
  }, [pickupDimensionTotals, deliveryDimensionTotals]);

  useEffect(() => {
    const nextLength = pointDimensionSummary.length > 0
      ? pointDimensionSummary.length.toFixed(2)
      : "";

    const nextWidth = pointDimensionSummary.width > 0
      ? pointDimensionSummary.width.toFixed(2)
      : "";

    const nextHeight = pointDimensionSummary.height > 0
      ? pointDimensionSummary.height.toFixed(2)
      : "";

    setLength(nextLength);
    setWidth(nextWidth);
    setHeight(nextHeight);

    if (nextLength) setLengthError("");
    if (nextWidth) setWidthError("");
    if (nextHeight) setHeightError("");
  }, [
    pointDimensionSummary.length,
    pointDimensionSummary.width,
    pointDimensionSummary.height,
  ]);

  const handleWeightChange = (e) => {
    let value = e.target.value;
    setWeight(value);

    const numericValue = parseFloat(value);

    if (isNaN(numericValue) || numericValue < 0.01) {
      setWeightError(t("errors.weightMin", { defaultValue: "Weight must be at least 0.01" }));
    } else {
      setWeightError("");
    }
  };

  const validateDeliveryAfterPickups = (deliveries = deliveryPlaces, pickups = pickUpPlaces) => {
    const pickupDateTimes = pickups
      .filter((p) => p.date && p.hour)
      .map((p) => new Date(`${p.date}T${p.hour}`));

    const errorIndexes = { date: [], hour: [] };

    deliveries.forEach((d, index) => {
      if (d.date && d.hour) {
        const deliveryDateTime = new Date(`${d.date}T${d.hour}`);

        for (const pickupDate of pickupDateTimes) {
          if (deliveryDateTime < pickupDate) {
            const deliveryDateOnly = d.date;
            const pickupDateOnly = pickupDate.toISOString().split("T")[0];

            if (deliveryDateOnly < pickupDateOnly) {
              errorIndexes.date.push(index);
            } else if (deliveryDateOnly === pickupDateOnly) {
              const deliveryTime = new Date(`1970-01-01T${d.hour}`);
              const pickupTime = new Date(
                `1970-01-01T${pickupDate.toISOString().split("T")[1].slice(0, 5)}`
              );
              if (deliveryTime < pickupTime) errorIndexes.hour.push(index);
            }
          }
        }
      }
    });

    return errorIndexes;
  };

  const validateAfterLoadBearer = () => {
    const invalidPickupIndexes = [];
    const invalidDeliveriesAfterBearerIndexes = [];

    if (loadBearerEarliestPickup) {
      pickUpPlaces.forEach((p, i) => {
        if (p.date && p.hour) {
          const date = new Date(`${p.date}T${p.hour}`);
          if (date < loadBearerEarliestPickup) invalidPickupIndexes.push(i);
        }
      });
    }

    if (loadBearerEarliestDelivery) {
      deliveryPlaces.forEach((d, i) => {
        if (d.date && d.hour) {
          const deliveryDate = new Date(`${d.date}T${d.hour}`);
          if (deliveryDate > loadBearerEarliestDelivery) {
            invalidDeliveriesAfterBearerIndexes.push({
              index: i,
              location: d.location,
              date: d.date,
              hour: d.hour,
            });
          }
        }
      });
    }

    return {
      invalidPickupIndexes,
      invalidDeliveriesAfterBearerIndexes,
      invalidDeliveryHourIndexes: [],
    };
  };

  const validatePlaces = (places, type = "pickup") => {
    return places.map((place) => {
      const locationError = !place.location?.trim();

      const lengthError =
        String(place.length ?? "").trim() !== ""
          ? getPointDimensionError("length", place.length)
          : "";

      const widthError =
        String(place.width ?? "").trim() !== ""
          ? getPointDimensionError("width", place.width)
          : "";

      const heightError =
        String(place.height ?? "").trim() !== ""
          ? getPointDimensionError("height", place.height)
          : "";

      const handlingEquipmentValue =
        String(place.handlingEquipment || "").trim().toUpperCase();

      const handlingEquipmentOtherError =
        handlingEquipmentValue === "OTHER" &&
        !String(place.handlingEquipmentOther || "").trim();

      return {
        ...place,
        locationError,
        dateError: false,
        hourError: false,
        addressError: false,
        postalCodeError: false,
        cityError: false,
        countryError: false,
        contactPhoneError: false,
        gMapsLinkError: false,
        coordinatesError: false,
        lengthError,
        widthError,
        heightError,
        loadingAccessError: false,
        handlingEquipmentError: false,
        handlingEquipmentOtherError,
        showMoreFields: place.showMoreFields,
      };
    });
  };

  const handleSaveChanges = async (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    let hasError = false;

    setFieldValidationError("");
    setPickUpPlaceError("");
    setDeliveryPlaceError("");
    setClientTypeError("");
    setClientSelectionError("");
    setNotes4driverError("");
    setNotes4subcontractedError("");
    setMerchandiseError("");
    // 🆕 trailer errors
    setTrailerTypeError("");
    setTrailerTypeOtherError("");

    // optional: simple max size guard
    if (notes4driver && notes4driver.length > 2000) {
      setNotes4driverError(t("errors.notesMax"));
      return;
    }

    if (showNotes4subcontracted && notes4subcontracted && notes4subcontracted.length > 2000) {
      setNotes4subcontractedError(t("errors.notesMax"));
      return;
    }

    const hasPickUps = pickUpPlaces.length > 0;
    const hasDeliveries = deliveryPlaces.length > 0;

    if (!hasPickUps && !hasDeliveries) {
      setFieldValidationError(t("errors.needPickupAndDelivery"));
      return;
    } else if (!hasPickUps) {
      setFieldValidationError(t("errors.needPickup"));
      return;
    } else if (!hasDeliveries) {
      setFieldValidationError(t("errors.needDelivery"));
      return;
    }

    const updatedPickups = validatePlaces(pickUpPlaces, "pickup");
    const updatedDeliveries = validatePlaces(deliveryPlaces, "delivery");

    setPickUpPlaces(updatedPickups);
    setDeliveryPlaces(updatedDeliveries);

    const hasInvalidPointFields = [
      ...updatedPickups,
      ...updatedDeliveries,
    ].some((place) => {
      return (
        place.locationError ||
        Boolean(place.lengthError) ||
        Boolean(place.widthError) ||
        Boolean(place.heightError) ||
        place.handlingEquipmentOtherError
      );
    });

    if (hasInvalidPointFields) {
      setFieldValidationError(t("errors.allRequiredFields"));
      return;
    }

    const toCents = (value) => Math.round(Number(value || 0) * 100);

    const pickupLengthTotal = updatedPickups.reduce((sum, point) => {
      return sum + Number(point.length || 0);
    }, 0);

    const deliveryLengthTotal = updatedDeliveries.reduce((sum, point) => {
      return sum + Number(point.length || 0);
    }, 0);

    const allPickupLengthsProvided =
      updatedPickups.length > 0 &&
      updatedPickups.every(
        (point) => String(point.length ?? "").trim() !== ""
      );

    const allDeliveryLengthsProvided =
      updatedDeliveries.length > 0 &&
      updatedDeliveries.every(
        (point) => String(point.length ?? "").trim() !== ""
      );

    if (
      allPickupLengthsProvided &&
      allDeliveryLengthsProvided &&
      toCents(pickupLengthTotal) !== toCents(deliveryLengthTotal)
    ) {
      const mismatchMessage = t("errors.pickupDeliveryLengthMismatch", {
        defaultValue: "❌ A soma dos comprimentos dos pontos de carga e descarga deve coincidir.",
      });

      setPickUpPlaceError(mismatchMessage);
      setDeliveryPlaceError(mismatchMessage);
      setFieldValidationError(mismatchMessage);

      setPickUpPlaces(
        updatedPickups.map((place) => ({
          ...place,
          lengthError: place.lengthError || t("errors.length.mismatch", {
            defaultValue: "❌ Os totais de comprimento das cargas e descargas devem coincidir.",
          }),
        }))
      );

      setDeliveryPlaces(
        updatedDeliveries.map((place) => ({
          ...place,
          lengthError: place.lengthError || t("errors.length.mismatch", {
            defaultValue: "❌ Os totais de comprimento das cargas e descargas devem coincidir.",
          }),
        }))
      );

      return;
    }

    if (
      String(length ?? "").trim() !== "" &&
      Number(length) > maxAllowedLength
    ) {
      setFieldValidationError(
        t("errors.lengthMax", {
          max: maxAllowedLength.toFixed(2),
        })
      );
      return;
    }

    const invalidIndexes = validateDeliveryAfterPickups();
    const mergedIndexes = [...new Set([...(invalidIndexes.date || []), ...(invalidIndexes.hour || [])])];
    setInvalidDeliveryIndexes(mergedIndexes);
    setDeliveryBeforePickupErrors(invalidIndexes);

    if (mergedIndexes.length > 0) {
      setDeliveryPlaceError(t("errors.deliveryBeforePickupGeneral"));
      return;
    }

    const bearerTimeValidationError = validateAfterLoadBearer();
    if (bearerTimeValidationError.errorMessage) {
      setFieldValidationError(bearerTimeValidationError.errorMessage);
      return;
    }

    setInvalidFields({ pickups: [], deliveries: [] });

    setClientTypeError("");
    setClientSelectionError("");
    setCarrierIdError("");

    const resolvedClientId =
      clientType === "industry"
        ? selectedClient?.value || industryClientId
        : clientType === "carrier"
          ? selectedClient?.value || carrierClientId
          : null;

    if (!clientType) {
      setClientTypeError(
        t("errors.clientTypeRequired", {
          defaultValue: "❌ Client type is required.",
        })
      );
      return;
    }

    if (!resolvedClientId) {
      setClientSelectionError(
        t("errors.clientRequired", {
          defaultValue: "❌ Client is required.",
        })
      );
      return;
    }

    const priceVal =
      String(price ?? "").trim() === ""
        ? null
        : parseFloat(String(price).replace(",", "."));

    if (priceVal === null) {
      setPriceError(
        t("errors.priceRequired", {
          defaultValue: "❌ Price is required.",
        })
      );
      return;
    }

    if (!Number.isFinite(priceVal) || priceVal < 0) {
      setPriceError(t("errors.pricePositive"));
      return;
    }

    setPriceError("");

    // Merchandise validation (required)
    if (!String(merchandise || "").trim()) {
      setMerchandiseError(t("errors.merchandiseRequired"));
      return;
    } else {
      setMerchandiseError("");
    }

    // 🆕 Trailer type is optional
    setTrailerTypeError("");

    if (String(trailerType || "").trim().toUpperCase() === "OTHER") {
      if (!String(trailerTypeOther || "").trim()) {
        setTrailerTypeOtherError(
          t("errors.trailerTypeOtherRequired", {
            defaultValue: "❌ Please specify the trailer type.",
          })
        );
        return;
      }

      setTrailerTypeOtherError("");
    } else {
      if (trailerTypeOther) setTrailerTypeOther("");
      setTrailerTypeOtherError("");
    }

    // Weight is optional, but validated when provided
    const weightVal =
      String(weight ?? "").trim() === ""
        ? null
        : parseFloat(String(weight).replace(",", "."));

    if (
      weightVal !== null &&
      (Number.isNaN(weightVal) || weightVal < 0.01)
    ) {
      setWeightError(
        t("errors.weightMin", {
          defaultValue: "Weight must be at least 0.01",
        })
      );
      return;
    }

    setWeightError("");
    setLoading(true);

    try {
      const resolvedIndustryId = selectedClient?.value || industryClientId || null;
      const resolvedCarrierId = selectedClient?.value || carrierClientId || null;

      const cleanNotes4driver =
        typeof notes4driver === "string" ? (notes4driver.trim() || null) : null;

      const cleanNotes4subcontracted =
        showNotes4subcontracted && typeof notes4subcontracted === "string"
          ? (notes4subcontracted.trim() || null)
          : null;

      const cleanPrice4subcontracted =
        showNotes4subcontracted && price4subcontractedInput !== "" && price4subcontractedInput != null
          ? Number(String(price4subcontractedInput).replace(",", "."))
          : null;

      await axios.put(`/loads/${loadId}`, {
        length:
          String(length ?? "").trim() === "" || !Number.isFinite(Number(length))
            ? null
            : Number(length),
        height:
          String(height ?? "").trim() === "" || !Number.isFinite(Number(height))
            ? null
            : Number(height),
        width:
          String(width ?? "").trim() === "" || !Number.isFinite(Number(width))
            ? null
            : Number(width),
        weight: weightVal,

        merchandise: String(merchandise || "").trim(),
        reference: String(reference || "").trim() || null,

        // 🆕 trailer type
        trailerType: String(trailerType || "").trim() || null,
        trailerTypeOther:
          String(trailerType || "").trim().toUpperCase() === "OTHER"
            ? (String(trailerTypeOther || "").trim() || null)
            : null,

        price: priceVal,
        priceCurrency: priceCurrency || "EUR",
        price4subcontrated: cleanPrice4subcontracted,

        notes4driver: cleanNotes4driver,
        notes4subcontracted: cleanNotes4subcontracted,    

        pickupLocations: updatedPickups.map((place) => ({
          location: place.location,
          date: place.date || null,
          hour: place.hour,

          // 🆕 point-level dimensions
          length: place.length === "" ? null : Number(place.length),
          width: place.width === "" ? null : Number(place.width),
          height: place.height === "" ? null : Number(place.height),

          placeId: place.placeId || null,
          address: place.address || "",
          postalCode: place.postalCode || "",
          city: place.city || "",
          country: place.country || "",
          gMapsLink: place.gMapsLink || "",
          coordinates: place.coordinates || "",
          contactPhone: place.contactPhone || "",
          loadingAccess: place.loadingAccess || null,
          handlingEquipment: place.handlingEquipment || null,
          handlingEquipmentOther:
            String(place.handlingEquipment || "").trim().toUpperCase() === "OTHER"
              ? (String(place.handlingEquipmentOther || "").trim() || null)
              : null,

          // 🆕 reference (optional)
          reference: String(place.reference || "").trim() || null,

        })),
        deliveryLocations: updatedDeliveries.map((place) => ({
          location: place.location,
          date: place.date || null,
          hour: place.hour,

          // 🆕 point-level dimensions
          length: place.length === "" ? null : Number(place.length),
          width: place.width === "" ? null : Number(place.width),
          height: place.height === "" ? null : Number(place.height),

          placeId: place.placeId || null,
          address: place.address || "",
          postalCode: place.postalCode || "",
          city: place.city || "",
          country: place.country || "",
          gMapsLink: place.gMapsLink || "",
          coordinates: place.coordinates || "",
          contactPhone: place.contactPhone || "",
          loadingAccess: place.loadingAccess || null,
          handlingEquipment: place.handlingEquipment || null,
          handlingEquipmentOther:
            String(place.handlingEquipment || "").trim().toUpperCase() === "OTHER"
              ? (String(place.handlingEquipmentOther || "").trim() || null)
              : null,

          // 🆕 reference (optional)
          reference: String(place.reference || "").trim() || null,
        })),

        // ✅ Denormalized for loads table
        pickupPoints: updatedPickups.map((p) => ({
          location: p.location,
          date: p.date || null,
          hour: p.hour || null,

          // 🆕 point-level dimensions
          length: p.length === "" ? null : Number(p.length),
          width: p.width === "" ? null : Number(p.width),
          height: p.height === "" ? null : Number(p.height),

          address: p.address || "",
          postalCode: p.postalCode || "",
          city: p.city || "",
          country: p.country || "",
          gMapsLink: p.gMapsLink || "",
          coordinates: p.coordinates || "",
          contactPhone: p.contactPhone || "",
          loadingAccess: p.loadingAccess || null,
          reference: p.reference || "",
          handlingEquipment: p.handlingEquipment || null,
          handlingEquipmentOther:
            String(p.handlingEquipment || "").trim().toUpperCase() === "OTHER"
              ? (String(p.handlingEquipmentOther || "").trim() || null)
              : null,
        })),
        deliveryPoints: updatedDeliveries.map((p) => ({
          location: p.location,
          date: p.date || null,
          hour: p.hour || null,

          // 🆕 point-level dimensions
          length: p.length === "" ? null : Number(p.length),
          width: p.width === "" ? null : Number(p.width),
          height: p.height === "" ? null : Number(p.height),

          address: p.address || "",
          postalCode: p.postalCode || "",
          city: p.city || "",
          country: p.country || "",
          gMapsLink: p.gMapsLink || "",
          coordinates: p.coordinates || "",
          contactPhone: p.contactPhone || "",
          loadingAccess: p.loadingAccess || null,
          reference: p.reference || "",
          handlingEquipment: p.handlingEquipment || null,
          handlingEquipmentOther:
            String(p.handlingEquipment || "").trim().toUpperCase() === "OTHER"
              ? (String(p.handlingEquipmentOther || "").trim() || null)
              : null,
        })),

        clientType: clientType || null,
        industryClientId:
          clientType === "industry" ? resolvedIndustryId : null,
        carrierClientId:
          clientType === "carrier" ? resolvedCarrierId : null,

        clientContactEmails: selectedClientContactEmails.map((opt) => opt.value),

          subcontractedEmails: showNotes4subcontracted
          ? selectedSubcontractedEmails.map((opt) => opt.value).slice(0, 3)
          : [],
        
        carrierId:
          !carrierId || carrierId === "__internal__"
            ? null
            : carrierId,
      });

      setSuccessPopupOpen(true);
    } catch (error) {
      console.error("Error updating super imposable load:", error);
      setLengthError(t("errors.updateFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handlePickUpPlaceChange = (event, index, field) => {
    const value = event.target.value;
    const updatedPlaces = [...pickUpPlaces];
    updatedPlaces[index][field] = value;

    if (field.endsWith("Error")) return;

    // limpa/atualiza erros ao escrever
    if (["length", "width", "height"].includes(field)) {
      updatedPlaces[index][`${field}Error`] = getPointDimensionError(field, value);
    }

    if (field === "location") updatedPlaces[index].locationError = false;
    if (field === "date") updatedPlaces[index].dateError = false;
    if (field === "hour") updatedPlaces[index].hourError = false;
    if (field === "address") updatedPlaces[index].addressError = false;
    if (field === "postalCode") updatedPlaces[index].postalCodeError = false;
    if (field === "city") updatedPlaces[index].cityError = false;
    if (field === "country") updatedPlaces[index].countryError = false;
    if (field === "contactPhone") updatedPlaces[index].contactPhoneError = false;
    if (field === "gMapsLink") updatedPlaces[index].gMapsLinkError = false;
    if (field === "coordinates") updatedPlaces[index].coordinatesError = false;

    setPickUpPlaces(updatedPlaces);

    const isValid = updatedPlaces.some((place) => place.location.trim() !== "");
    setPickUpPlaceError(isValid ? "" : t("errors.atLeastOnePickup"));

    const updatedInvalidIndexes = validateDeliveryAfterPickups(deliveryPlaces, updatedPlaces);
    const merged = [...(updatedInvalidIndexes?.date || []), ...(updatedInvalidIndexes?.hour || [])];
    setInvalidDeliveryIndexes(merged);
    setDeliveryBeforePickupErrors(updatedInvalidIndexes);

    const bearerValidation = validateAfterLoadBearer();
    setInvalidFields({
      pickups: bearerValidation.invalidPickupIndexes,
      deliveries: bearerValidation.invalidDeliveryIndexes,
    });
    setInvalidBearerDeliveryConflicts(bearerValidation.invalidDeliveryIndexes || []);
  };

  const handleDeliveryPlaceChange = (event, index, field) => {
    const value = event.target.value;
    const updatedPlaces = [...deliveryPlaces];
    updatedPlaces[index][field] = value;

    if (field.endsWith("Error")) return;

    // limpa/atualiza erros ao escrever
    if (["length", "width", "height"].includes(field)) {
      updatedPlaces[index][`${field}Error`] = getPointDimensionError(field, value);
    }

    if (field === "location") updatedPlaces[index].locationError = false;
    if (field === "date") updatedPlaces[index].dateError = false;
    if (field === "hour") updatedPlaces[index].hourError = false;
    if (field === "address") updatedPlaces[index].addressError = false;
    if (field === "postalCode") updatedPlaces[index].postalCodeError = false;
    if (field === "city") updatedPlaces[index].cityError = false;
    if (field === "country") updatedPlaces[index].countryError = false;
    if (field === "contactPhone") updatedPlaces[index].contactPhoneError = false;
    if (field === "gMapsLink") updatedPlaces[index].gMapsLinkError = false;
    if (field === "coordinates") updatedPlaces[index].coordinatesError = false;

    setDeliveryPlaces(updatedPlaces);

    const updatedErrors = validateDeliveryAfterPickups(updatedPlaces, pickUpPlaces);
    const mergedIndexes = [...new Set([...(updatedErrors.date || []), ...(updatedErrors.hour || [])])];

    setInvalidDeliveryIndexes(mergedIndexes);
    setDeliveryBeforePickupErrors(updatedErrors);

    const bearerValidation = validateAfterLoadBearer();
    setInvalidFields({
      pickups: bearerValidation.invalidPickupIndexes,
      deliveries: bearerValidation.invalidDeliveriesAfterBearerIndexes,
    });
    setInvalidBearerDeliveryConflicts(bearerValidation.invalidDeliveriesAfterBearerIndexes || []);
    setInvalidBearerDeliveryHourIndexes(bearerValidation.invalidDeliveryHourIndexes || []);
  };

  // 🆕 subcontracted price
  const handlePrice4subcontractedChange = (e) => {
    const v = e.target.value;
    setPrice4subcontractedInput(v);

    if (v === "" || v === null) {
      setPrice4subcontractedError("");
      return;
    }
    const n = Number(String(v).replace(",", "."));
    if (Number.isNaN(n) || n < 0) setPrice4subcontractedError(t("errors.pricePositive"));
    else setPrice4subcontractedError("");
  };  

  const removePickUpPlace = (index) => {
    const updated = [...pickUpPlaces];
    updated.splice(index, 1);
    setPickUpPlaces(updated);
  };

  const removeDeliveryPlace = (index) => {
    const updated = [...deliveryPlaces];
    updated.splice(index, 1);
    setDeliveryPlaces(updated);
  };

  return (
    <div className="edit-superimposable-container">
      <div ref={pageTopRef} />
      <div className="edit-superimposable-back-arrow" onClick={() => navigate("/loads")}>
        ← {t("nav.back")}
      </div>

      {successPopupOpen && (
        <div
          className="edit-superimposable-popupOverlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-superimposable-success-title"
        >
          <div className="edit-superimposable-popupModal">
            <div className="edit-superimposable-popupIconWrap">
              <FaCheckCircle className="edit-superimposable-popupIcon" />
            </div>

            <h2
              id="edit-superimposable-success-title"
              className="edit-superimposable-popupTitle"
            >
              {t("popup.updated_title")}
            </h2>

            <p className="edit-superimposable-popupMessage">
              {t("popup.updated_message")}
            </p>

            <div className="edit-superimposable-popupActions">
              <button
                type="button"
                className="edit-superimposable-popupPrimaryBtn"
                onClick={handleCloseSuccessPopup}
              >
                {t("popup.ok")}
              </button>
            </div>
          </div>
        </div>
      )}

      <h1 className="edit-superimposable-title">{t("title")}</h1>

      <div className="edit-superimposable-card">
        <form className="edit-superimposable-form" onSubmit={handleSaveChanges}>
        
        {/* Top meta + audit */}
          <div className="edit-superimposable-topbar">
            <div className="edit-superimposable-row">
              <div className="edit-superimposable-kv-row">
                <span
                  className="edit-superimposable-kv edit-superimposable-kv-pair"
                  title={
                    tsCounter != null
                      ? t("kv.sessionTooltip", { n: tsCounter })
                      : load?.transportsessionId
                      ? t("kv.sessionIdTooltip", { id: load.transportsessionId })
                      : ""
                  }
                >
                  <span className="edit-superimposable-kv__key">{t("kv.session")}:</span>
                  <span className="edit-superimposable-kv__value">{tsCounter ?? "—"}</span>
                </span>

                <span className="edit-superimposable-kv__sep">•</span>

                <span
                  className="edit-superimposable-kv edit-superimposable-kv-pair"
                  title={
                    loadCounter != null
                      ? t("kv.loadTooltip", { n: loadCounter })
                      : load?.id
                      ? t("kv.loadIdTooltip", { id: load.id })
                      : ""
                  }
                >
                  <span className="edit-superimposable-kv__key">{t("kv.load")}:</span>
                  <span className="edit-superimposable-kv__value">{loadCounter ?? "—"}</span>
                </span>
              </div>
            </div>

            <div className="edit-superimposable-audit-inline">
              <span
                className="edit-superimposable-audit-chip"
                title={displayAuditEmail(load?.createdByEmail ?? prefill?.createdByEmail)}
              >
                <span className="edit-superimposable-audit-chip-label">
                  {t("labels.createdByEmail", { defaultValue: "Created by" })}
                </span>
                <span className="edit-superimposable-audit-chip-value">
                  {displayAuditEmail(load?.createdByEmail ?? prefill?.createdByEmail)}
                </span>
              </span>

              <span
                className="edit-superimposable-audit-chip"
                title={displayAuditEmail(load?.updatedByEmail ?? prefill?.updatedByEmail)}
              >
                <span className="edit-superimposable-audit-chip-label">
                  {t("labels.updatedByEmail", { defaultValue: "Updated by" })}
                </span>
                <span className="edit-superimposable-audit-chip-value">
                  {displayAuditEmail(load?.updatedByEmail ?? prefill?.updatedByEmail)}
                </span>
              </span>
            </div>
          </div>

          {/* Columns layout */}
          <div className="edit-superimposable-columns">
            {/* Column 1 - Pick-Up Points */}
            <div className="edit-superimposable-column">
              <h3 className="edit-superimposable-column-title">{t("columns.loadingPoints")}</h3>

              {(() => {
                console.log("🧱 pickUpPlaces:", pickUpPlaces);

                if (!Array.isArray(pickUpPlaces)) {
                  console.error("❌ pickUpPlaces is not an array!");
                } else {
                  pickUpPlaces.forEach((p, i) => {
                    if (!p) {
                      console.error(`❌ Entry at index ${i} is undefined or null`, p);
                    } else {
                      console.log(`✅ Entry ${i}:`, p);
                      if (!("hourError" in p)) {
                        console.warn(`⚠️ hourError missing in entry ${i}:`, p);
                      }
                    }
                  });
                }
              })()}

              {pickUpPlaces.map((place, index) => (
                <div key={index} className="edit-superimposable-multi-location-input">
                  <div className="edit-superimposable-label-row">
                    <label className="edit-superimposable-points-label-points">
                      {t("labels.pickupPlace")}*
                    </label>

                    {pickUpPlaces.length > 1 && (
                      <button
                        type="button"
                        className="edit-superimposable-delete-btn"
                        onClick={() => removePickUpPlace(index)}
                        title={t("tooltips.removePickup")}
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  <div className="edit-superimposable-select-wrapper-left-icon">
                    <CreatableSelect
                      isClearable
                      isMulti={false}
                      options={placeOptions}
                      placeholder={t("placeholders.selectOrTypePlace")}
                      value={resolvePlaceSelectValue(place, placeOptions)}
                      formatOptionLabel={renderPlaceOption}
                      onChange={(selectedOption) => {
                        const updated = [...pickUpPlaces];
                        if (selectedOption?.__isNew__) {
                          updated[index] = {
                            ...updated[index],
                            placeId: null,
                            location: selectedOption.label,
                            address: "",
                            postalCode: "",
                            city: "",
                            country: "",
                            gMapsLink: "",
                            coordinates: "",
                            contactPhone: "",
                            // 🔴 limpa os erros
                            locationError: false,
                            addressError: false,
                            postalCodeError: false,
                            cityError: false,
                            countryError: false,
                            gMapsLinkError: false,
                            coordinatesError: false,
                            contactPhoneError: false,
                            loadingAccess: null,          // ✅
                            loadingAccessError: false,    // ✅
                            handlingEquipment: null,
                            handlingEquipmentOther: "",
                            handlingEquipmentError: false,
                            handlingEquipmentOtherError: false,
                            reference: updated[index]?.reference ?? "",
                          };
                        } else if (selectedOption?.fullData) {
                          const p = selectedOption.fullData;
                          updated[index] = {
                            ...updated[index],
                            placeId: p.id,
                            location: p.placeName,
                            address: p.address,
                            postalCode: p.postalCode,
                            city: p.city,
                            country: p.country,
                            gMapsLink: p.gMapsLink,
                            coordinates: p.coordinates,
                            contactPhone: p.contactPhone,
                            // 🆕
                            loadingAccess: p.loadingAccess ?? null,
                            loadingAccessError: false,
                            handlingEquipment: null,
                            handlingEquipmentOther: "",
                            handlingEquipmentError: false,
                            handlingEquipmentOtherError: false,
                            // ✅ limpa erros
                            locationError: false,
                            addressError: false,
                            postalCodeError: false,
                            cityError: false,
                            countryError: false,
                            gMapsLinkError: false,
                            coordinatesError: false,
                            contactPhoneError: false,
                          };
                        } else {
                          updated[index] = {
                            ...updated[index],
                            placeId: null,
                            location: selectedOption?.label || "",
                            address: "",
                            postalCode: "",
                            city: "",
                            country: "",
                            gMapsLink: "",
                            coordinates: "",
                            contactPhone: "",
                            // ✅ limpa erros
                            locationError: false,
                            addressError: false,
                            postalCodeError: false,
                            cityError: false,
                            countryError: false,
                            gMapsLinkError: false,
                            coordinatesError: false,
                            contactPhoneError: false,
                            loadingAccess: null,          // ✅
                            loadingAccessError: false,    // ✅
                            handlingEquipment: null,
                            handlingEquipmentOther: "",
                            handlingEquipmentError: false,
                            handlingEquipmentOtherError: false,
                          };
                        }

                        setPickUpPlaces(updated);
                      }}
                      styles={customSelectStyles}
                      classNamePrefix={
                        place.locationError ? "edit-superimposable-select-error" : "edit-superimposable-select"
                      }
                    />
                  </div>

                  {formSubmitted && place.locationError && (
                    <p className="edit-superimposable-error">{t("errors.locationRequired")}</p>
                  )}

                  <div className="edit-superimposable-datetime-group">
                    <div className="edit-superimposable-custom-input-wrapper">
                      <input
                        type="date"
                        value={place.date || ""}
                        onChange={(e) => {
                          const updated = [...pickUpPlaces];
                          updated[index].date = e.target.value;
                          updated[index].dateError = false;
                          setPickUpPlaces(updated);
                        }}
                        onBlur={() => {
                          const updated = [...pickUpPlaces];
                          updated[index].dateError = false;
                          setPickUpPlaces(updated);
                        }}
                        className={`edit-superimposable-input ${place.dateError ? "input-error" : ""}`}
                        ref={(el) => (dateRefs.current[index] = el)}
                        onMouseDown={(e) => {
                          const el = e.currentTarget;
                          if (document.activeElement !== el) el.focus();
                          if (typeof el.showPicker === "function") {
                            try {
                              el.showPicker();
                            } catch {}
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            const el = e.currentTarget;
                            if (typeof el.showPicker === "function") {
                              try {
                                el.showPicker();
                              } catch {}
                            }
                          }
                        }}
                      />
                      <FaCalendarAlt
                        className="edit-superimposable-input-icon clickable"
                        onClick={(e) => {
                          e.preventDefault();
                          const input = dateRefs.current[index];
                          if (input) {
                            input.focus();
                            if (typeof input.showPicker === "function") {
                              input.showPicker();
                            }
                          }
                        }}
                      />
                    </div>

                    <div className="edit-superimposable-custom-input-wrapper">
                      <input
                        type="text"
                        placeholder={t("placeholders.hhmm")}
                        value={place.hour || ""}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/[^0-9]/g, "");
                          let formatted = raw;
                          if (raw.length >= 3) {
                            formatted = `${raw.slice(0, 2)}:${raw.slice(2, 4)}`;
                          }

                          const updated = [...pickUpPlaces];
                          updated[index].hour = formatted;

                          if (!formatted) {
                            updated[index].hourError = false;
                          } else {
                            const regex = /^([01]\d|2[0-3]):([0-5]\d)$/;
                            updated[index].hourError = !regex.test(formatted);
                          }

                          setPickUpPlaces(updated);
                          setFieldValidationError("");
                        }}
                        className={`edit-superimposable-input ${place.hourError ? "input-error" : ""}`}
                        ref={(el) => (timeRefs.current[index] = el)}
                      />
                      <FaClock
                        className="edit-superimposable-input-icon clickable"
                        onClick={(e) => {
                          e.preventDefault();
                          const input = timeRefs.current[index];
                          if (input) {
                            input.focus();
                            if (typeof input.showPicker === "function") {
                              input.showPicker();
                            }
                          }
                        }}
                      />
                    </div>
                  </div>

                  {place.hourError && String(place.hour || "").trim() !== "" && (
                    <p className="edit-superimposable-error">{t("errors.hourFormat")}</p>
                  )}

                  <div className="edit-superimposable-point-dimensions-block">
                    <label className="edit-superimposable-label edit-superimposable-point-dimensions-title">
                      {t("labels.pointDimensions", {
                        defaultValue: "Dimensões da carga neste ponto (em metros)",
                      })}
                    </label>

                    <div className="edit-superimposable-point-dimensions-row">
                      <div className="edit-superimposable-point-dimension-field">
                        <label>{t("labels.pointLengthShort", { defaultValue: "Comprimento" })}</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={place.length || ""}
                          onChange={(e) => handlePickUpPlaceChange(e, index, "length")}
                          className={`edit-superimposable-input ${place.lengthError ? "input-error" : ""}`}
                          placeholder="0.00m"
                        />
                        {place.lengthError && (
                          <p className="edit-superimposable-error">{place.lengthError}</p>
                        )}
                      </div>

                      <div className="edit-superimposable-point-dimension-field">
                        <label>{t("labels.pointWidthShort", { defaultValue: "Largura" })}</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={place.width || ""}
                          onChange={(e) => handlePickUpPlaceChange(e, index, "width")}
                          className={`edit-superimposable-input ${place.widthError ? "input-error" : ""}`}
                          placeholder="0.00m"
                        />
                        {place.widthError && (
                          <p className="edit-superimposable-error">{place.widthError}</p>
                        )}
                      </div>

                      <div className="edit-superimposable-point-dimension-field">
                        <label>{t("labels.pointHeightShort", { defaultValue: "Altura" })}</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={place.height || ""}
                          onChange={(e) => handlePickUpPlaceChange(e, index, "height")}
                          className={`edit-superimposable-input ${place.heightError ? "input-error" : ""}`}
                          placeholder="0.00m"
                        />
                        {place.heightError && (
                          <p className="edit-superimposable-error">{place.heightError}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ✅ Loading Access */}
                  <label className="edit-superimposable-label" style={{ marginTop: -8 }}>
                    {t("loadingAccess.label", { defaultValue: "Loading access" })}
                  </label>

                  <div className="edit-superimposable-select-wrapper" style={{ marginTop: -8, marginBottom: 5 }}>
                    <Select
                      options={loadingAccessOptions}
                      value={
                        loadingAccessOptions.find(
                          (o) => o.value === String(place.loadingAccess || "").trim().toUpperCase()
                        ) || null
                      }
                      onChange={(opt) => handleLoadingAccessChange(index, opt, false)}
                      isClearable
                      placeholder={t("loadingAccess.placeholder", { defaultValue: "Select loading access..." })}
                      styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      classNamePrefix={
                        place.loadingAccessError ? "edit-superimposable-select-error" : "edit-superimposable-select"
                      }
                      menuPortalTarget={document.body}
                    />
                  </div>

                  {formSubmitted && place.loadingAccessError && (
                    <p className="edit-superimposable-error">
                      {t("errors.loadingAccessRequired", { defaultValue: "❌ Loading access is required." })}
                    </p>
                  )}

                  {/* ✅ Handling Equipment */}
                  <label className="edit-superimposable-label" style={{ marginTop: -8 }}>
                    {t("fields.handlingEquipment.label", { defaultValue: "Handling equipment" })}
                  </label>

                  <div className="edit-superimposable-select-wrapper" style={{ marginTop: -8, marginBottom: 5 }}>
                    <Select
                      options={handlingEquipmentOptions}
                      value={
                        handlingEquipmentOptions.find(
                          (o) => o.value === String(place.handlingEquipment || "").trim().toUpperCase()
                        ) || null
                      }
                      onChange={(opt) => handleHandlingEquipmentChange(index, opt, false)}
                      isClearable
                      placeholder={t("fields.handlingEquipment.placeholder", { defaultValue: "Select equipment..." })}
                      styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      classNamePrefix={
                        (place.handlingEquipmentError || place.handlingEquipmentOtherError)
                          ? "edit-superimposable-select-error"
                          : "edit-superimposable-select"
                      }
                      menuPortalTarget={document.body}
                    />
                  </div>

                  {String(place.handlingEquipment || "").trim().toUpperCase() === "OTHER" && (
                    <div className="edit-superimposable-field-group" style={{ marginTop: -8, marginBottom: 5 }}>
                      <label className="edit-superimposable-label">
                        {t("fields.handlingEquipmentOther", { defaultValue: "Other (specify)" })}
                      </label>

                      <input
                        type="text"
                        placeholder={t("fields.handlingEquipmentOtherPlaceholder", { defaultValue: "Write the equipment..." })}
                        value={place.handlingEquipmentOther || ""}
                        onChange={(e) => {
                          const v = e.target.value ?? "";
                          setPickUpPlaces((prev) => {
                            const updated = [...prev];
                            if (!updated[index]) return prev;
                            updated[index] = {
                              ...updated[index],
                              handlingEquipmentOther: v,
                              handlingEquipmentOtherError: false,
                            };
                            return updated;
                          });
                        }}
                        className={`edit-superimposable-input ${place.handlingEquipmentOtherError ? "input-error" : ""}`}
                      />

                      {formSubmitted && place.handlingEquipmentOtherError && (
                        <p className="edit-superimposable-error">
                          {t("errors.handlingEquipmentOtherRequired", { defaultValue: "❌ Please specify the equipment." })}
                        </p>
                      )}
                    </div>
                  )}

                  {/* 🆕 Reference (optional) */}
                  <div className="edit-superimposable-field-group" style={{ marginTop: -6, marginBottom: 10 }}>
                    <label className="edit-superimposable-label">
                      {t("labels.reference", { defaultValue: "Reference" })}
                    </label>

                    <input
                      type="text"
                      placeholder={t("placeholders.reference", { defaultValue: "e.g.: PO 12345, customer ref..." })}
                      value={place.reference || ""}
                      onChange={(e) => {
                        const v = e.target.value ?? "";
                        setPickUpPlaces((prev) => {
                          const updated = [...prev];
                          if (!updated[index]) return prev;
                          updated[index] = { ...updated[index], reference: v };
                          return updated;
                        });
                      }}
                      className="edit-superimposable-input"
                    />
                  </div>

                  {place.showMoreFields ? (
                    <>
                      <input
                        type="text"
                        placeholder={t("placeholders.address")}
                        value={place.address}
                        onChange={(e) => handlePickUpPlaceChange(e, index, "address")}
                        className={`edit-load-input edit-load-main-field ${place.addressError ? "input-error" : ""}`}
                      />

                      <div className="edit-superimposable-row-group">
                        <input
                          type="text"
                          placeholder={t("placeholders.postalCode")}
                          value={place.postalCode}
                          onChange={(e) => handlePickUpPlaceChange(e, index, "postalCode")}
                          className={`edit-load-input edit-load-main-field ${
                            place.postalCodeError ? "input-error" : ""
                          }`}
                        />
                        <input
                          type="text"
                          placeholder={t("placeholders.city")}
                          value={place.city}
                          onChange={(e) => handlePickUpPlaceChange(e, index, "city")}
                          className={`edit-load-input edit-load-main-field ${place.cityError ? "input-error" : ""}`}
                        />
                        <input
                          type="text"
                          placeholder={t("placeholders.country")}
                          value={place.country}
                          onChange={(e) => handlePickUpPlaceChange(e, index, "country")}
                          className={`edit-load-input edit-load-main-field ${place.countryError ? "input-error" : ""}`}
                        />
                      </div>

                      <input
                        type="text"
                        placeholder={t("placeholders.contactPhone")}
                        value={place.contactPhone}
                        onChange={(e) => handlePickUpPlaceChange(e, index, "contactPhone")}
                        className={`edit-load-input edit-load-main-field ${
                          place.contactPhoneError ? "input-error" : ""
                        }`}
                      />

                      <input
                        type="text"
                        placeholder={t("placeholders.gMapsLink")}
                        value={place.gMapsLink}
                        onChange={(e) => handlePickUpPlaceChange(e, index, "gMapsLink")}
                        className={`edit-load-input edit-load-main-field ${place.gMapsLinkError ? "input-error" : ""}`}
                      />

                      <input
                        type="text"
                        placeholder={t("placeholders.coordinates")}
                        value={place.coordinates}
                        onChange={(e) => handlePickUpPlaceChange(e, index, "coordinates")}
                        className={`edit-load-input edit-load-main-field ${
                          place.coordinatesError ? "input-error" : ""
                        }`}
                      />

                      <div className="edit-superimposable-toggle-fields-wrapper">
                        <button
                          type="button"
                          className="edit-superimposable-toggle-fields-btn"
                          onClick={() => {
                            const updated = [...pickUpPlaces];
                            updated[index].showMoreFields = false;
                            setPickUpPlaces(updated);
                          }}
                        >
                          {t("buttons.hideFields")} <FiChevronUp className="edit-superimposable-toggle-arrow" />
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="edit-superimposable-toggle-fields-wrapper">
                      <button
                        type="button"
                        className="edit-superimposable-toggle-fields-btn"
                        onClick={() => {
                          const updated = [...pickUpPlaces];
                          updated[index].showMoreFields = true;
                          setPickUpPlaces(updated);
                        }}
                      >
                        {t("buttons.moreFields")} <FiChevronDown className="edit-superimposable-toggle-arrow" />
                      </button>
                    </div>
                  )}
                </div>
              ))}

              {pickUpPlaces.length < MAX_POINTS ? (
                <button
                  type="button"
                  className="edit-superimposable-add-location-btn"
                  onClick={() =>
                    setPickUpPlaces((prev) => [
                      ...prev,
                      normalizePoint({
                        location: "",
                        date: "",
                        hour: "",
                        placeId: null,
                        address: "",
                        postalCode: "",
                        city: "",
                        country: "",
                        gMapsLink: "",
                        coordinates: "",
                        contactPhone: "",
                        loadingAccess: null,
                        handlingEquipment: null,
                        handlingEquipmentOther: "",
                        reference: "", // 🆕
                      }),
                    ])
                  }
                >
                  {t("buttons.addAnotherPickup")}
                </button>
              ) : (
                <p className="edit-superimposable-error">{t("warnings.maxPickupReached")}</p>
              )}

              {pickUpPlaceError && <p className="edit-superimposable-error">{pickUpPlaceError}</p>}
            </div>

            {/* Column 2 - Merchandise Info */}
            <div className="edit-superimposable-column">
              <h3 className="edit-superimposable-column-title">{t("columns.merchandise")}</h3>

              <div className="edit-superimposable-merchandise-inline-row">
                <div className="edit-superimposable-field-group edit-superimposable-weight-field">
                  <label className="edit-superimposable-label">
                    {t("labels.weight", { defaultValue: "Peso" })} (kg)
                  </label>
                  <div className="edit-superimposable-input-container">
                    <input
                      type="number"
                      value={weight}
                      onChange={handleWeightChange}
                      className={`edit-superimposable-input ${weightError ? "input-error" : ""}`}
                      step="0.01"
                      min="0"
                      placeholder="0"
                    />
                  </div>
                  {weightError && <p className="edit-superimposable-error">{weightError}</p>}
                </div>

                <div className="edit-superimposable-field-group edit-superimposable-merchandise-field">
                  <label className="edit-superimposable-label">{t("labels.merchandise")}*</label>
                  <div className="edit-superimposable-input-container">
                    <input
                      type="text"
                      value={merchandise}
                      onChange={(e) => {
                        const v = e.target.value;
                        setMerchandise(v);
                        if (merchandiseError && String(v).trim()) setMerchandiseError("");
                      }}
                      placeholder={t("placeholders.merchandise")}
                      className={`edit-superimposable-input ${merchandiseError ? "input-error" : ""}`}
                    />
                  </div>
                  {merchandiseError && <p className="edit-superimposable-error">{merchandiseError}</p>}
                </div>
              </div>

              <div className="edit-superimposable-load-dimensions-summary">
                <label className="edit-superimposable-label edit-superimposable-load-dimensions-title">
                  {t("labels.loadDimensions", {
                    defaultValue: "Dimensões totais da carga",
                  })}
                </label>

                <div className="edit-superimposable-load-dimensions-row">
                  <div className="edit-superimposable-load-dimension-field">
                    <label>{t("labels.length", { defaultValue: "Comprimento" })} (m)</label>
                    <input
                      type="text"
                      value={length}
                      readOnly
                      className="edit-superimposable-input edit-superimposable-auto-calculated-input"
                      placeholder="0.00"
                    />
                  </div>

                  <div className="edit-superimposable-load-dimension-field">
                    <label>{t("labels.width", { defaultValue: "Largura" })} (m)</label>
                    <input
                      type="text"
                      value={width}
                      readOnly
                      className="edit-superimposable-input edit-superimposable-auto-calculated-input"
                      placeholder="0.00"
                    />
                  </div>

                  <div className="edit-superimposable-load-dimension-field">
                    <label>{t("labels.height", { defaultValue: "Altura" })} (m)</label>
                    <input
                      type="text"
                      value={height}
                      readOnly
                      className="edit-superimposable-input edit-superimposable-auto-calculated-input"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {!pointDimensionSummary.lengthMatches && (
                  <p className="edit-superimposable-dimension-warning-text">
                    {t("errors.pickupDeliveryLengthMismatch", {
                      defaultValue: "⚠️ A soma dos comprimentos dos pontos de carga e descarga ainda não coincide.",
                    })}
                  </p>
                )}

                <div className="edit-superimposable-dimension-helper-text">
                  {t("labels.loadDimensionsHelper", {
                    defaultValue:
                      "Comprimento calculado pela soma dos pontos. Largura e altura usam o maior valor registado.",
                  })}
                </div>
              </div>

              {/* 🆕 Trailer Type */}
              <div className="edit-superimposable-field-group">
                <label className="edit-superimposable-label">
                  {t("fields.trailerType.label", { defaultValue: "Trailer type" })}
                </label>

                <Select
                  options={trailerTypeOptions}
                  value={
                    trailerType
                      ? trailerTypeOptions.find((o) => String(o.value) === String(trailerType).trim().toUpperCase()) || {
                          value: trailerType,
                          label: String(trailerType),
                        }
                      : null
                  }
                  onChange={(opt) => {
                    const v = opt?.value ? String(opt.value) : "";
                    setTrailerType(v);
                    setTrailerTypeError("");

                    if (String(v).toUpperCase() !== "OTHER") {
                      setTrailerTypeOther("");
                      setTrailerTypeOtherError("");
                    }
                  }}
                  isClearable
                  isSearchable
                  placeholder={t("fields.trailerType.placeholder", { defaultValue: "Select trailer type..." })}
                  styles={customSelectStyles}
                  classNamePrefix={trailerTypeError || (formSubmitted && !trailerType) ? "select-error" : "select"}
                />

                {trailerTypeError && <p className="edit-special-load-error-client">{trailerTypeError}</p>}
              </div>

              {/* 🆕 Trailer Type Other (only when OTHER) */}
              {isTrailerOther && (
                <div className="edit-superimposable-field-group">
                  <label className="edit-superimposable-label">
                    {t("fields.trailerTypeOther.label", { defaultValue: "Other (specify)" })}
                  </label>

                  <input
                    type="text"
                    value={trailerTypeOther}
                    onChange={(e) => {
                      const v = e.target.value ?? "";
                      setTrailerTypeOther(v);
                      if (trailerTypeOtherError && String(v).trim()) setTrailerTypeOtherError("");
                    }}
                    placeholder={t("fields.trailerTypeOther.placeholder", { defaultValue: "Write the trailer type..." })}
                    className={`edit-superimposable-input ${trailerTypeOtherError ? "input-error" : ""}`}
                  />

                  {trailerTypeOtherError && <p className="edit-superimposable-error">{trailerTypeOtherError}</p>}
                </div>
              )}

              <h3 className="edit-superimposable-column-title">{t("columns.generalInformation")}</h3>

              <div className="edit-superimposable-field-group">
                <label className="edit-superimposable-label">{t("labels.price")}</label>

                <div className={`edit-superimposable-price-wrapper ${priceError ? "input-error" : ""}`}>
                  <input
                    type="number"
                    step="0.01"
                    inputMode="decimal"
                    value={price}
                    onChange={(e) => {
                      const v = e.target.value;
                      setPrice(v);
                      const num = parseFloat(String(v).replace(",", "."));
                      if (!String(v).trim()) {
                        setPriceError(t("errors.priceRequiredShort"));
                      } else if (Number.isNaN(num) || num < 0) {
                        setPriceError(t("errors.pricePositive"));
                      } else {
                        setPriceError("");
                      }
                    }}
                    className="edit-superimposable-input edit-superimposable-price-input"
                    placeholder={t("placeholders.price")}
                  />

                  <div className="edit-superimposable-price-currency">
                    <Select
                      options={CURRENCY_OPTIONS}
                      value={
                        CURRENCY_OPTIONS.find((opt) => opt.value === (priceCurrency || "EUR")) ||
                        CURRENCY_OPTIONS[0]
                      }
                      onChange={(opt) => setPriceCurrency(opt?.value || "EUR")}
                      isClearable={false}
                      isSearchable={false}
                      styles={{
                        ...customSelectStyles,
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      classNamePrefix="edit-superimposable-select"
                      menuPortalTarget={document.body}
                    />
                  </div>
                </div>

                {priceError && <p className="edit-superimposable-error">{priceError}</p>}
              </div>

              {/* 🆕 Price for Subcontracted (ONLY when external carrier) */}
              {showNotes4subcontracted && (
                <div className="edit-superimposable-field-group">
                  <label className="edit-superimposable-label">
                    {t("fields.price4subcontracted.label", { defaultValue: "Preço do subcontratado" })}
                  </label>

                  <div className={`edit-superimposable-price-wrapper ${price4subcontractedError ? "input-error" : ""}`}>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      inputMode="decimal"
                      placeholder={t("fields.price4subcontracted.placeholder", { defaultValue: "0.00" })}
                      value={price4subcontractedInput}
                      onChange={handlePrice4subcontractedChange}
                      className="edit-superimposable-input edit-superimposable-price-input"
                    />

                    <div className="edit-superimposable-price-currency">
                      <Select
                        options={CURRENCY_OPTIONS}
                        value={
                          CURRENCY_OPTIONS.find((opt) => opt.value === (priceCurrency || "EUR")) ||
                          CURRENCY_OPTIONS[0]
                        }
                        onChange={(opt) => setPriceCurrency(opt?.value || "EUR")}
                        isClearable={false}
                        isSearchable={false}
                        styles={{
                          ...customSelectStyles,
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                        classNamePrefix="edit-superimposable-select"
                        menuPortalTarget={document.body}
                      />
                    </div>
                  </div>

                  {price4subcontractedError && <p className="edit-superimposable-error">{price4subcontractedError}</p>}
                </div>
              )}

              {/* Client Type */}
              <div className="edit-superimposable-field-group">
                <label className="edit-superimposable-label">{t("labels.clientType")}</label>
                <Select
                  options={CLIENT_TYPE_OPTIONS}
                  value={clientType ? CLIENT_TYPE_OPTIONS.find((o) => o.value === clientType) : null}
                  onChange={(opt) => {
                    const v = opt?.value ?? "";
                    setClientType(v);
                    setSelectedClient(null);
                    setSelectedClientContactEmails([]);
                    // reset dos IDs quando mudas o tipo
                    setIndustryClientId("");
                    setCarrierClientId("");
                  }}
                  isClearable
                  isSearchable={false}
                  styles={customSelectStyles}
                  classNamePrefix="select"
                  placeholder={t("placeholders.chooseType")}
                />
              </div>

              {/* Client Selection (depende do tipo) */}
              {clientType && (
                <div className="edit-superimposable-field-group">
                  <label className="edit-superimposable-label">
                    {clientType === "industry" ? t("labels.industryClient") : t("labels.carrierClient")}
                  </label>
                  <Select
                    options={clientType === "industry" ? industryOptions : carrierOptions}
                    value={
                      clientType === "industry"
                        ? resolveIdSelectValue(
                            industryClientId,
                            industryOptions,
                            prefill?.industryClientName || `#${industryClientId}`
                          )
                        : resolveIdSelectValue(
                            carrierClientId,
                            carrierOptions,
                            prefill?.carrierClientName || `#${carrierClientId}`
                          )
                    }
                    onChange={(opt) => {
                      setSelectedClient(opt || null);
                      setSelectedClientContactEmails([]);

                      if (clientType === "industry") setIndustryClientId(opt ? opt.value : "");
                      else setCarrierClientId(opt ? opt.value : "");
                    }}
                    isClearable
                    isSearchable
                    styles={customSelectStyles}
                    classNamePrefix="select"
                    placeholder={
                      clientType === "industry"
                        ? t("placeholders.selectIndustryClient")
                        : t("placeholders.selectCarrierClient")
                    }
                  />

                  {((clientType === "industry" && industryClientId) ||
                    (clientType === "carrier" && carrierClientId)) && (
                    <div className="edit-superimposable-field-group">
                      <label className="edit-superimposable-label">
                        {t("fields.clientContactEmails.label", {
                          defaultValue: "Client contact email(s)",
                        })}
                      </label>

                      <Select
                        isMulti
                        isClearable
                        closeMenuOnSelect={false}
                        options={selectedClientContactEmailOptions}
                        value={selectedClientContactEmails}
                        onChange={(selectedOptions) => {
                          const limited = Array.isArray(selectedOptions)
                            ? selectedOptions.slice(0, 3)
                            : [];
                          setSelectedClientContactEmails(limited);
                        }}
                        placeholder={t("fields.clientContactEmails.placeholder", {
                          defaultValue: "Select up to 3 contact email(s)...",
                        })}
                        noOptionsMessage={() =>
                          t("fields.clientContactEmails.noOptions", {
                            defaultValue: "No contact emails were found for this client.",
                          })
                        }
                        styles={multiSelectHorizontalStyles}
                        classNamePrefix="select"
                        menuPortalTarget={document.body}
                      />

                      <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
                        {t("fields.clientContactEmails.helper", {
                          defaultValue: "These emails come from the client's contact list.",
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Transport Carrier */}
              <div className="edit-superimposable-field-group">
                <label className="edit-superimposable-label">{t("labels.transportCarrier")}</label>
                <Select
                  options={carrierOptionsForSelect}
                  value={
                    carrierId
                      ? carrierOptionsForSelect.find((o) => String(o.value) === String(carrierId)) || {
                          value: carrierId,
                          label: prefill?.carrierName || `#${carrierId}`,
                        }
                      : null
                  }
                  onChange={(opt) => {
                    setCarrierId(opt ? String(opt.value) : "");
                    setCarrierIdError("");
                    setSelectedSubcontractedEmails([]);
                  }}
                  isClearable
                  isSearchable
                  placeholder={t("placeholders.selectTransportCarrier")}
                  styles={customSelectStyles}
                  classNamePrefix="select"
                />

                {showNotes4subcontracted && (
                  <div className="edit-superimposable-field-group">
                    <label className="edit-superimposable-label">
                      {t("fields.subcontractedEmail.label", {
                        defaultValue: "Subcontracted Emails",
                      })}
                    </label>

                    <Select
                      isMulti
                      isClearable
                      closeMenuOnSelect={false}
                      options={selectedSubcontractedEmailOptions}
                      value={selectedSubcontractedEmails}
                      onChange={(selectedOptions) => {
                        const limited = Array.isArray(selectedOptions)
                          ? selectedOptions.slice(0, 3)
                          : [];
                        setSelectedSubcontractedEmails(limited);
                      }}
                      placeholder={t("fields.subcontractedEmail.placeholder", {
                        defaultValue: "Select up to 3 subcontracted emails...",
                      })}
                      noOptionsMessage={() =>
                        t("fields.subcontractedEmail.noOptions", {
                          defaultValue: "No contact emails found for this subcontracted carrier.",
                        })
                      }
                      styles={multiSelectHorizontalStyles}
                      classNamePrefix="select"
                      menuPortalTarget={document.body}
                    />

                    <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
                      {t("fields.subcontractedEmail.helper", {
                        defaultValue:
                          "These emails come from the subcontracted carrier contact list. Maximum: 3.",
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Column 3 - Delivery Points */}
            <div className="edit-superimposable-column">
              <h3 className="edit-superimposable-column-title">{t("columns.unloadingPoints")}</h3>

              {deliveryPlaces.map((place, index) => (
                <div key={index} className="edit-superimposable-multi-location-input">
                  <div className="edit-superimposable-label-row">
                    <label className="edit-superimposable-points-label-points">
                      {t("labels.deliveryPlace")}*
                    </label>
                    {deliveryPlaces.length > 1 && (
                      <button
                        type="button"
                        className="edit-superimposable-delete-btn"
                        onClick={() => removeDeliveryPlace(index)}
                        title={t("tooltips.removeDelivery")}
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  <div className="edit-superimposable-select-wrapper-left-icon">
                    <CreatableSelect
                      isClearable
                      isMulti={false}
                      options={placeOptions}
                      placeholder={t("placeholders.selectOrTypePlace")}
                      value={resolvePlaceSelectValue(place, placeOptions)}
                      formatOptionLabel={renderPlaceOption}
                      onChange={(selectedOption) => {
                        const updated = [...deliveryPlaces];
                        const base = {
                          placeId: null,
                          location: "",
                          address: "",
                          postalCode: "",
                          city: "",
                          country: "",
                          gMapsLink: "",
                          coordinates: "",
                          contactPhone: "",

                          // 🆕 preserve reference
                          reference: updated[index]?.reference ?? "",

                          // 🆕
                          loadingAccess: null,
                          loadingAccessError: false,

                          // 🆕
                          handlingEquipment: null,
                          handlingEquipmentOther: "",
                          handlingEquipmentError: false,
                          handlingEquipmentOtherError: false,
                          // ✅ limpa todos os erros
                          locationError: false,
                          addressError: false,
                          postalCodeError: false,
                          cityError: false,
                          countryError: false,
                          gMapsLinkError: false,
                          coordinatesError: false,
                          contactPhoneError: false,
                          reference: updated[index]?.reference ?? "",
                        };

                        if (selectedOption?.__isNew__) {
                          updated[index] = { ...updated[index], ...base, location: selectedOption.label };
                        } else if (selectedOption?.fullData) {
                          const p = selectedOption.fullData;
                          updated[index] = {
                            ...updated[index],
                            ...base,
                            placeId: p.id,
                            location: p.placeName,
                            address: p.address,
                            postalCode: p.postalCode,
                            city: p.city,
                            country: p.country,
                            gMapsLink: p.gMapsLink,
                            coordinates: p.coordinates,
                            contactPhone: p.contactPhone,
                            // 🆕
                            loadingAccess: p.loadingAccess ?? null,
                            loadingAccessError: false,
                            handlingEquipment: null,
                            handlingEquipmentOther: "",
                            handlingEquipmentError: false,
                            handlingEquipmentOtherError: false,
                          };
                        } else {
                          updated[index] = { ...updated[index], ...base, location: selectedOption?.label || "" };
                        }

                        setDeliveryPlaces(updated);
                      }}
                      styles={customSelectStyles}
                      classNamePrefix={
                        place.locationError ? "edit-superimposable-select-error" : "edit-superimposable-select"
                      }
                    />
                  </div>

                  {formSubmitted && place.locationError && (
                    <p className="edit-superimposable-error">{t("errors.locationRequired")}</p>
                  )}

                  <div className="edit-superimposable-datetime-group">
                    <div className="edit-superimposable-custom-input-wrapper">
                      <input
                        type="date"
                        value={place.date || ""}
                        onChange={(e) => {
                          const updated = [...deliveryPlaces];
                          updated[index].date = e.target.value;
                          updated[index].dateError = false;
                          setDeliveryPlaces(updated);
                        }}
                        onBlur={() => {
                          const updated = [...deliveryPlaces];
                          updated[index].dateError = false;
                          setDeliveryPlaces(updated);
                        }}
                        className={`edit-superimposable-input ${place.dateError ? "input-error" : ""}`}
                        ref={(el) => (deliveryDateRefs.current[index] = el)}
                        onMouseDown={(e) => {
                          const el = e.currentTarget;
                          if (document.activeElement !== el) el.focus();
                          if (typeof el.showPicker === "function") {
                            try {
                              el.showPicker();
                            } catch {}
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            const el = e.currentTarget;
                            if (typeof el.showPicker === "function") {
                              try {
                                el.showPicker();
                              } catch {}
                            }
                          }
                        }}
                      />
                      <FaCalendarAlt
                        className="edit-superimposable-input-icon clickable"
                        onClick={(e) => {
                          e.preventDefault();
                          const input = deliveryDateRefs.current[index];
                          if (input) {
                            input.focus();
                            if (typeof input.showPicker === "function") input.showPicker();
                          }
                        }}
                      />
                    </div>

                    <div className="edit-superimposable-custom-input-wrapper">
                      <input
                        type="text"
                        placeholder={t("placeholders.hhmm")}
                        value={place.hour || ""}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/[^0-9]/g, "");
                          let formatted = raw;
                          if (raw.length >= 3) {
                            formatted = `${raw.slice(0, 2)}:${raw.slice(2, 4)}`;
                          }

                          const updated = [...deliveryPlaces];
                          updated[index].hour = formatted;

                          if (!formatted) {
                            updated[index].hourError = false;
                          } else {
                            const regex = /^([01]\d|2[0-3]):([0-5]\d)$/;
                            updated[index].hourError = !regex.test(formatted);
                          }

                          setDeliveryPlaces(updated);
                          setFieldValidationError("");
                        }}
                        onBlur={() => {
                          const updatedErrors = validateDeliveryAfterPickups(deliveryPlaces, pickUpPlaces);
                          const mergedIndexes = [
                            ...new Set([...(updatedErrors.date || []), ...(updatedErrors.hour || [])]),
                          ];

                          setInvalidDeliveryIndexes(mergedIndexes);
                          setDeliveryBeforePickupErrors(updatedErrors);

                          const bearerValidation = validateAfterLoadBearer();
                          setFieldValidationError(bearerValidation.errorMessage);
                          setInvalidFields({
                            pickups: bearerValidation.invalidPickupIndexes,
                            deliveries: bearerValidation.invalidDeliveryIndexes,
                          });
                          setInvalidBearerDeliveryConflicts(
                            bearerValidation.invalidDeliveriesAfterBearerIndexes || []
                          );
                        }}
                        ref={(el) => (deliveryTimeRefs.current[index] = el)}
                        className={`edit-superimposable-input ${place.hourError ? "input-error" : ""}`}
                      />
                      <FaClock
                        className="edit-superimposable-input-icon clickable"
                        onClick={(e) => {
                          e.preventDefault();
                          const input = deliveryTimeRefs.current[index];
                          if (input) {
                            input.focus();
                            if (typeof input.showPicker === "function") input.showPicker();
                          }
                        }}
                      />
                    </div>
                  </div>

                  {formSubmitted &&
                    place.hourError &&
                    String(place.hour || "").trim() !== "" && (
                      <p className="edit-superimposable-error">{t("errors.hourFormat")}</p>
                    )}

                  <div className="edit-superimposable-point-dimensions-block">
                    <label className="edit-superimposable-label edit-superimposable-point-dimensions-title">
                      {t("labels.pointDimensions", {
                        defaultValue: "Dimensões da carga neste ponto (em metros)",
                      })}*
                    </label>

                    <div className="edit-superimposable-point-dimensions-row">
                      <div className="edit-superimposable-point-dimension-field">
                        <label>{t("labels.pointLengthShort", { defaultValue: "Comprimento" })}</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={place.length || ""}
                          onChange={(e) => handleDeliveryPlaceChange(e, index, "length")}
                          className={`edit-superimposable-input ${place.lengthError ? "input-error" : ""}`}
                          placeholder="0.00m"
                        />
                        {place.lengthError && (
                          <p className="edit-superimposable-error">{place.lengthError}</p>
                        )}
                      </div>

                      <div className="edit-superimposable-point-dimension-field">
                        <label>{t("labels.pointWidthShort", { defaultValue: "Largura" })}</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={place.width || ""}
                          onChange={(e) => handleDeliveryPlaceChange(e, index, "width")}
                          className={`edit-superimposable-input ${place.widthError ? "input-error" : ""}`}
                          placeholder="0.00m"
                        />
                        {place.widthError && (
                          <p className="edit-superimposable-error">{place.widthError}</p>
                        )}
                      </div>

                      <div className="edit-superimposable-point-dimension-field">
                        <label>{t("labels.pointHeightShort", { defaultValue: "Altura" })}</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={place.height || ""}
                          onChange={(e) => handleDeliveryPlaceChange(e, index, "height")}
                          className={`edit-superimposable-input ${place.heightError ? "input-error" : ""}`}
                          placeholder="0.00m"
                        />
                        {place.heightError && (
                          <p className="edit-superimposable-error">{place.heightError}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ✅ Loading Access */}
                  <label className="edit-superimposable-label" style={{ marginTop: -8 }}>
                    {t("loadingAccess.label", { defaultValue: "Loading access" })}
                  </label>

                  <div className="edit-superimposable-select-wrapper" style={{ marginTop: -8, marginBottom: 5}}>
                    <Select
                      options={loadingAccessOptions}
                      value={
                        loadingAccessOptions.find(
                          (o) => o.value === String(place.loadingAccess || "").trim().toUpperCase()
                        ) || null
                      }
                      onChange={(opt) => handleLoadingAccessChange(index, opt, true)}
                      isClearable
                      placeholder={t("loadingAccess.placeholder", { defaultValue: "Select loading access..." })}
                      styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      classNamePrefix={
                        place.loadingAccessError ? "edit-superimposable-select-error" : "edit-superimposable-select"
                      }
                      menuPortalTarget={document.body}
                    />
                  </div>

                  {formSubmitted && place.loadingAccessError && (
                    <p className="edit-superimposable-error">
                      {t("errors.loadingAccessRequired", { defaultValue: "❌ Loading access is required." })}
                    </p>
                  )}

                  {/* ✅ Handling Equipment */}
                  <label className="edit-superimposable-label" style={{ marginTop: -8 }}>
                    {t("fields.handlingEquipment.label", { defaultValue: "Handling equipment" })}
                  </label>

                  <div className="edit-superimposable-select-wrapper" style={{ marginTop: -8, marginBottom: 10 }}>
                    <Select
                      options={handlingEquipmentOptions}
                      value={
                        handlingEquipmentOptions.find(
                          (o) => o.value === String(place.handlingEquipment || "").trim().toUpperCase()
                        ) || null
                      }
                      onChange={(opt) => handleHandlingEquipmentChange(index, opt, true)}
                      isClearable
                      placeholder={t("fields.handlingEquipment.placeholder", { defaultValue: "Select equipment..." })}
                      styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      classNamePrefix={
                        (place.handlingEquipmentError || place.handlingEquipmentOtherError)
                          ? "edit-superimposable-select-error"
                          : "edit-superimposable-select"
                      }
                      menuPortalTarget={document.body}
                    />
                  </div>

                  {String(place.handlingEquipment || "").trim().toUpperCase() === "OTHER" && (
                    <div className="edit-superimposable-field-group" style={{ marginTop: -6, marginBottom: 10 }}>
                      <label className="edit-superimposable-label">
                        {t("fields.handlingEquipmentOther", { defaultValue: "Other (specify)" })}
                      </label>

                      <input
                        type="text"
                        placeholder={t("fields.handlingEquipmentOtherPlaceholder", { defaultValue: "Write the equipment..." })}
                        value={place.handlingEquipmentOther || ""}
                        onChange={(e) => {
                          const v = e.target.value ?? "";
                          setDeliveryPlaces((prev) => {
                            const updated = [...prev];
                            if (!updated[index]) return prev;
                            updated[index] = {
                              ...updated[index],
                              handlingEquipmentOther: v,
                              handlingEquipmentOtherError: false,
                            };
                            return updated;
                          });
                        }}
                        className={`edit-superimposable-input ${place.handlingEquipmentOtherError ? "input-error" : ""}`}
                      />

                      {formSubmitted && place.handlingEquipmentOtherError && (
                        <p className="edit-superimposable-error">
                          {t("errors.handlingEquipmentOtherRequired", { defaultValue: "❌ Please specify the equipment." })}
                        </p>
                      )}
                    </div>
                  )}

                  {/* 🆕 Reference (optional) */}
                  <div className="edit-superimposable-field-group" style={{ marginTop: -6, marginBottom: 10 }}>
                    <label className="edit-superimposable-label">
                      {t("labels.reference", { defaultValue: "Reference" })}
                    </label>

                    <input
                      type="text"
                      placeholder={t("placeholders.reference", { defaultValue: "e.g.: PO 12345, customer ref..." })}
                      value={place.reference || ""}
                      onChange={(e) => {
                        const v = e.target.value ?? "";
                        setDeliveryPlaces((prev) => {
                          const updated = [...prev];
                          if (!updated[index]) return prev;
                          updated[index] = { ...updated[index], reference: v };
                          return updated;
                        });
                      }}
                      className="edit-superimposable-input"
                    />
                  </div>

                  {place.showMoreFields ? (
                    <>
                      <input
                        type="text"
                        placeholder={t("placeholders.address")}
                        value={place.address || ""}
                        onChange={(e) => handleDeliveryPlaceChange(e, index, "address")}
                        className={`edit-load-input edit-load-main-field ${place.addressError ? "input-error" : ""}`}
                      />

                      <div className="edit-superimposable-row-group">
                        <input
                          type="text"
                          placeholder={t("placeholders.postalCode")}
                          value={place.postalCode || ""}
                          onChange={(e) => handleDeliveryPlaceChange(e, index, "postalCode")}
                          className={`edit-load-input edit-load-main-field ${
                            place.postalCodeError ? "input-error" : ""
                          }`}
                        />

                        <input
                          type="text"
                          placeholder={t("placeholders.city")}
                          value={place.city || ""}
                          onChange={(e) => handleDeliveryPlaceChange(e, index, "city")}
                          className={`edit-load-input edit-load-main-field ${place.cityError ? "input-error" : ""}`}
                        />

                        <input
                          type="text"
                          placeholder={t("placeholders.country")}
                          value={place.country || ""}
                          onChange={(e) => handleDeliveryPlaceChange(e, index, "country")}
                          className={`edit-load-input edit-load-main-field ${place.countryError ? "input-error" : ""}`}
                        />
                      </div>

                      <input
                        type="text"
                        placeholder={t("placeholders.contactPhone")}
                        value={place.contactPhone || ""}
                        onChange={(e) => handleDeliveryPlaceChange(e, index, "contactPhone")}
                        className={`edit-load-input edit-load-main-field ${
                          place.contactPhoneError ? "input-error" : ""
                        }`}
                      />

                      <input
                        type="text"
                        placeholder={t("placeholders.gMapsLink")}
                        value={place.gMapsLink || ""}
                        onChange={(e) => handleDeliveryPlaceChange(e, index, "gMapsLink")}
                        className={`edit-load-input edit-load-main-field ${place.gMapsLinkError ? "input-error" : ""}`}
                      />

                      <input
                        type="text"
                        placeholder={t("placeholders.coordinatesExample")}
                        value={place.coordinates || ""}
                        onChange={(e) => handleDeliveryPlaceChange(e, index, "coordinates")}
                        className={`edit-load-input edit-load-main-field ${
                          place.coordinatesError ? "input-error" : ""
                        }`}
                      />

                      <div className="edit-superimposable-toggle-fields-wrapper">
                        <button
                          type="button"
                          className="edit-superimposable-toggle-fields-btn"
                          onClick={() => {
                            const updated = [...deliveryPlaces];
                            updated[index].showMoreFields = false;
                            setDeliveryPlaces(updated);
                          }}
                        >
                          {t("buttons.hideFields")} <FiChevronUp className="edit-superimposable-toggle-arrow" />
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="edit-superimposable-toggle-fields-wrapper">
                      <button
                        type="button"
                        className="edit-superimposable-toggle-fields-btn"
                        onClick={() => {
                          const updated = [...deliveryPlaces];
                          updated[index].showMoreFields = true;
                          setDeliveryPlaces(updated);
                        }}
                      >
                        {t("buttons.moreFields")} <FiChevronDown className="edit-superimposable-toggle-arrow" />
                      </button>
                    </div>
                  )}

                  {(Array.isArray(invalidDeliveryIndexes) && invalidDeliveryIndexes.includes(index)) ||
                  (Array.isArray(invalidBearerDeliveryConflicts) &&
                    invalidBearerDeliveryConflicts.some((c) => c.index === index)) ? (
                    <>
                      {Array.isArray(deliveryBeforePickupErrors.date) &&
                        deliveryBeforePickupErrors.date.includes(index) && (
                          <p className="edit-superimposable-error">{t("errors.deliveryDateBeforePickup")}</p>
                        )}
                      {Array.isArray(deliveryBeforePickupErrors.hour) &&
                        deliveryBeforePickupErrors.hour.includes(index) && (
                          <p className="edit-superimposable-error">{t("errors.deliveryHourBeforePickup")}</p>
                        )}
                    </>
                  ) : null}
                </div>
              ))}

              {deliveryPlaces.length < MAX_POINTS ? (
                <button
                  type="button"
                  className="edit-superimposable-add-location-btn"
                  onClick={() =>
                    setDeliveryPlaces((prev) => [
                      ...prev,
                      normalizePoint({
                        location: "",
                        date: "",
                        hour: "",
                        placeId: null,
                        address: "",
                        postalCode: "",
                        city: "",
                        country: "",
                        gMapsLink: "",
                        coordinates: "",
                        contactPhone: "",
                        loadingAccess: null,
                        handlingEquipment: null,
                        handlingEquipmentOther: "",
                        reference: "", // 🆕
                      }),
                    ])
                  }
                >
                  {t("buttons.addAnotherDelivery")}
                </button>
              ) : (
                <p className="edit-superimposable-error">{t("warnings.maxDeliveryReached")}</p>
              )}

              {deliveryPlaceError && <p className="edit-superimposable-error">{deliveryPlaceError}</p>}
            </div>
          </div>

          
          {/* 🆕 Notes for Subcontracted (ONLY when carrierId !== companyId) */}
          {showNotes4subcontracted && (
            <div className="edit-superimposable-field-group">
              <label className="edit-superimposable-label">
                {t("labels.notesForSubcontracted")}
              </label>
              <textarea
                rows={3}
                placeholder={t("placeholders.notesForSubcontracted")}
                value={notes4subcontracted}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v.length <= 2000) {
                    setNotes4subcontracted(v);
                    if (notes4subcontractedError) setNotes4subcontractedError("");
                  } else {
                    setNotes4subcontractedError(t("errors.notesMax"));
                  }
                }}
                className={`edit-superimposable-input ${notes4subcontractedError ? "input-error" : ""}`}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 12,
                  opacity: 0.75,
                  marginTop: 4,
                }}
              >
                <span>{t("notes.helper")}</span>
                <span>{(notes4subcontracted || "").length}/2000</span>
              </div>
              {notes4subcontractedError && (
                <p className="edit-superimposable-error">{notes4subcontractedError}</p>
              )}
            </div>
          )}



          {/* Notes for Driver — full width below columns */}
          <div className="edit-superimposable-field-group">
            <label className="edit-superimposable-label">{t("labels.notesForDriver")}</label>
            <textarea
              rows={3}
              placeholder={t("placeholders.notesForDriver")}
              value={notes4driver}
              onChange={(e) => {
                const v = e.target.value;
                if (v.length <= 2000) {
                  setNotes4driver(v);
                  if (notes4driverError) setNotes4driverError("");
                } else {
                  setNotes4driverError(t("errors.notesMax"));
                }
              }}
              className={`edit-superimposable-input ${notes4driverError ? "input-error" : ""}`}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 12,
                opacity: 0.75,
              }}
            >
              <span>{t("notesHelper.visibleInDriverApp")}</span>
              <span>
                {notes4driver.length}/2000
              </span>
            </div>
            {notes4driverError && <p className="edit-superimposable-error">{notes4driverError}</p>}
          </div>

          {/* Buttons */}
          <div className="edit-superimposable-button-group">
            <button
              type="button"
              onClick={() => navigate(`/loads`)}
              className="edit-superimposable-cancel-button"
            >
              {t("buttons.cancel")}
            </button>

            <button
              type="submit"
              className="edit-superimposable-save-button"
              disabled={
                loading ||
                weightError ||
                priceError ||
                merchandiseError ||
                trailerTypeError ||
                trailerTypeOtherError
              }
            >
              {loading ? t("buttons.saving") : t("buttons.saveChanges")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditSuperImposableLoad;
